// Page: usage-bayou  (utility-usage — Bayou variant: full bill-upload UI PLUS
// the Bayou customer/token onboarding flow. Essentially the base `usage/form`
// module + Bayou, with the XCEL-CO-SF premises sub-flow and the token-expiry /
// nine-day refresh path.)
//
// Ported from the inline <script> in
//   Enrollment_modules/usage-bayou/form.module/module.html (lines 326–4360).
//
// MECHANICAL / FAITHFUL PORT — runtime behaviour is preserved. Deliberate changes:
//   1. Selectors scoped to the render wrapper `<div class="lgsiayeu-main">` are now
//      scoped to `root` (the element carrying data-ea-module) via q/qi/qa. Kept
//      document-scoped, exactly as the original: `document.querySelector('body')`,
//      `document.querySelectorAll('button')`/`('a')`; the loader/overlay lookups
//      `getElementById('myUsageLoader')`/`('MUloaderOverlay')`/
//      `('PassUpdatedresetPassLoader')`/`('PassUpdatedloaderOverlay')` (via `di`);
//      and the cross-widget `.page1lmileftright_myusage #goback-personal-info`
//      (sibling widget, NOT inside lgsiayeu-main) via `dq`. Everything else in this
//      module's own markup is root-scoped. The Bayou.loadOnboardingForm target,
//      originally `document.getElementById("\have-online-details")` (the `\h`
//      non-escape resolves to id "have-online-details"), is root-scoped to
//      `q("#have-online-details")`.
//   2. `_hcms/api/*` fetches use core/api `apiUrl(...)` (incl. the already
//      root-relative `LogToNautilus` call); the same-origin redirects
//      (`enrollment.nautilussolar.com/my-info`, `/`, `/myusage`, `/my-consent`)
//      are now root-relative ("/my-info", "/", "/myusage", "/my-consent"). Note the
//      error-path redirect suffix is `/myusage` in the source (no hyphen) — kept.
//   3. The three UAN-config + session-config HubDB read URLs are built with
//      core/hubdb `hubdbRowsUrl` (keys `uanConfig`, `utilityAvailability`) instead
//      of inline `api.hubapi.com` literals.
//   4. localStorage goes through core/storage. Keys outside the StorageKey union
//      use raw*: `bayou_contact_id`, `bayou_reason`, `BayouContactId`,
//      `is_enrolled_for_bayou`, `isWeBackFromTYP`, `uan1Value`, `uan2Value`,
//      `UanNumber`, `utilityProvider`, `rp`, `uan_2_internal_value`.
//   5. Four EXTERNAL non-portal hosts have NO __ENV__ key and are kept as literal
//      absolute URLs (faithful — inventing an env key is scope creep): the AWS
//      file-upload service (uploadFileToServer) and the Hansen/Bayou endpoints
//      `https://hansen.danmarcrm.com/{getAccountNumbers,bayouCreateCustomer,
//      getContactByID}.php` (all https). This variant does NOT read
//      `window.__ENV__.bayouApiUrl`, so none is introduced. Flagged, not rewritten.
//      (The `dev2hansen.danmarcrm.com` string inside the LogToNautilus body is a
//      data value, kept literal.)
//   6. The formerly-inline onclick/ondrop/ondragover handlers (toggleContent,
//      dropHandler, dragOverHandler, triggerFilePicker, triggerFileInput,
//      removeImageOpenPopup, removeCurrentImage, removeImageClosePopup) are stripped
//      from the markup and re-bound root-scoped via a local bind() helper at the end
//      of init — no page function leaks onto `window`. The commented-out camera
//      blocks (captureFromCamera / bare triggerFileInput) stay commented in markup;
//      their fns are `void`-ed here.
//   7. `Bayou` is the CDN global from the kept `js.bayou.energy/v1` <script> tag —
//      declared ambiently (module-local) below, exactly like usage-il-lmi-bayou.ts.
//      `Sentry`, `Swiper`, `pdfjsLib` are declared ambiently in the theme-root
//      global.d.ts. `crypt` is not referenced by this module's live code (all crypt
//      calls are commented out), but the crypto-js provider + `var crypt` glue stay
//      in module.html untouched.
//   8. `// NOTE:` marks preserved latent quirks: the `document.getElementsById`
//      typo in getDataFromSessionConfigTable (cast, unreachable); and the three
//      implicit-global writes `meter_id`/`utility_type`/`rate_class` in the skip
//      branch of addDataToDealRecord (declared as locals — write-only / dead).
//
// Dynamic ES5 patterns (vars reassigned across types, Bayou/HubDB row shapes,
// DataTransfer/File/IndexedDB shapes) are typed `any` to keep the port mechanical.

import { apiUrl } from "../../core/api"
import { hubdbRowsUrl } from "../../core/hubdb"
import * as storage from "../../core/storage"
import { beginOperation, type LoadingOperationHandle } from "../../ui/loading-modal"

// Bayou onboarding widget, loaded via the CDN <script src="https://js.bayou.energy/v1">
// tag kept in module.html. Typed loosely — no @types in this theme.
declare const Bayou: any

export function init(root: HTMLElement, _config: unknown): void {
__ea_trace("modules/usage/usage-bayou.ts","init",[root,_config],["root","_config"]);
  // ─── Scoped DOM helpers ──────────────────────────────────────────────────────
  const q = (sel: string) => root.querySelector(sel) as HTMLElement
  const qi = (sel: string) => root.querySelector(sel) as HTMLInputElement
  const qa = (sel: string) => root.querySelectorAll(sel)
  const di = (id: string) => document.getElementById(id) as HTMLElement
  const dq = (sel: string) => document.querySelector(sel) as HTMLElement

  // ─── Shared state (was module-scoped / hoisted in the inline script) ──────────
  let credNotVerified = false
  let manualAccountNumber = false
  let responseFromAPI: any = ""

  let verifyidenty: any
  const bayouFormCompleted = false
  void bayouFormCompleted
  let bayouToken: any = null
  let bayouTokenType: any = null
  let bayouUtility: any = null
  let bayouId: any = null
  let bayouAccountNo: any = null
  let firstId: any = null
  let meterId: any = null
  let meterType: any = null
  let rateClass: any = null
  let additionalAttributeItem: any = null
  let additionalAttributeValue: any = null
  let accountNumberResponse = ""
  let accountNofuncCallCount = 0
  let mfaRequired = "No"
  let premisesId: any = null
  let premisesAddress = ""

  let _selectRadioValue: any
  let isChecked = false
  const storedDisplayElementList: any = JSON.parse(storage.get("usageDisplayelementList") ?? "null")
  const storedRequireElementList: any = JSON.parse(storage.get("usagerequireElementList") ?? "null")
  void storedRequireElementList
  const shouldValidate = false
  void shouldValidate

  // utilityNumberRegexFromSessionTable
  const UtilityAccountNumberRegexErrorMsg: any[] = []
  const utRgxNumber: any[] = []
  let utRgxNumberInput: any
  let UtilityAccountNumberRegexErrorMsgInput: any
  let uanSingleValue = ""
  let uanSingleErrorMsgField: any
  let clickutilityAccountInputs: any
  const allAccountNumbers: any[] = []
  const uanAllLabels: any[] = []
  const uanFieldDefault: any[] = []
  const uanFieldDisplayarray: any[] = []
  const uanFieldIntDesArray: any[] = []

  let globPass: any
  void globPass
  let enrollmentSessionID: any
  const utilityIdd = storage.get("utility_id")

  const isInvalidFileType = false
  void isInvalidFileType
  const isExceededSize = false
  void isExceededSize
  const listOfFiles: any[] = []
  let swiper: any
  const uploadedurl: any[] = []
  void uploadedurl

  const limit = 1000
  let offset = 0
  let allRows: any[] = []

  // Module-level flag reassigned by the checked-skip block below; read by the
  // GetDataFromDealRecord* flows (which run after that block).
  let isWeBackFromTYP = false

  let db: any
  const is_enrolled_for_bayou = storage.rawGet("is_enrolled_for_bayou")

  pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.11.338/pdf.worker.min.js"

  // ─── Loading modal ─────────────────────────────────────────────────────────
  // Mechanical migration of the former fake-timer progress bar to the shared,
  // accessible loading modal (js/src/ui/loading-modal.ts). This was the "leaky"
  // variant whose setInterval kept running after closeProgressBar() hid the
  // overlay; that leak (and the fabricated percentage) are gone by construction.
  // Kind "mutation" so a genuine hang shows honest copy with a Close escape. A
  // deferred audit will turn the Bayou polling/delay into an abort-aware read
  // operation.
  let loaderHandle: LoadingOperationHandle | null = null
  function openProgressBar(message = "We are getting in contact with your utility service provider."): void {
__ea_trace("modules/usage/usage-bayou.ts","openProgressBar",[message],["message"]);
    loaderHandle = beginOperation({ kind: "mutation", message })
  }
  function closeProgressBar(): void {
__ea_trace("modules/usage/usage-bayou.ts","closeProgressBar",[],[]);
    loaderHandle?.complete()
    loaderHandle = null
  }

  // ─── loader function ──────────────────────────────────────────────────────────
  function pageFoundOpenLoader(): void {
__ea_trace("modules/usage/usage-bayou.ts","pageFoundOpenLoader",[],[]);
    const loader = di("myUsageLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MUloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }
  function pageFoundCloseLoader(): void {
__ea_trace("modules/usage/usage-bayou.ts","pageFoundCloseLoader",[],[]);
    const loader = di("myUsageLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MUloaderOverlay").style.display = "none"
    loader.style.display = "none"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[button],["button"]);
      button.disabled = false
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = false
    })
  }
  function passUpdatedOpenLoader(): void {
__ea_trace("modules/usage/usage-bayou.ts","passUpdatedOpenLoader",[],[]);
    const loader = di("PassUpdatedresetPassLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PassUpdatedloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }

  // ─── Get saved data ────────────────────────────────────────────────────────────
  function getValidTariff(meter: any): any {
__ea_trace("modules/usage/usage-bayou.ts","getValidTariff",[meter],["meter"]);
    if (!meter.tariffs || meter.tariffs.length === 0) {
      return null // no tariffs at all
    }

    let selectedTariff: any

    if (meter.tariffs.length === 1) {
      // single tariff
      selectedTariff = meter.tariffs[0]
    } else {
      // multiple tariffs → pick the one with biggest period_to
      selectedTariff = meter.tariffs.reduce((latest: any, current: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[latest,current],["latest","current"]);
        return new Date(current.period_to) > new Date(latest.period_to) ? current : latest
      })
    }

    // check if selected tariff period_to is more than 1 year older than today
    const periodToDate = new Date(selectedTariff.period_to)
    const today = new Date()

    const oneYearLater = new Date(today)
    oneYearLater.setFullYear(today.getFullYear() + 1)

    const oneYearAgo = new Date(today)
    oneYearAgo.setFullYear(today.getFullYear() - 1)

    const isMoreThanOneYearFuture = periodToDate > oneYearLater
    const isOlderThanOneYearPast = periodToDate < oneYearAgo

    // You can control account number visibility here
    const showAccountNumber = isMoreThanOneYearFuture || isOlderThanOneYearPast

    return {
      tariff: selectedTariff,
      isMoreThanOneYear: showAccountNumber,
    }
  }

  /* --------------- for first enrollment ----------------------- */
  async function GetDataFromDealRecord(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","GetDataFromDealRecord",[],[]);
    pageFoundOpenLoader()
    await BayouCreateCustomer()
    // var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
      } else if (dataa.message === "User Found") {
        // var userName = dataa['userValue']['properties']['utility_username'];
        // var utilityPass = dataa['userValue']['properties']['utility_password'];
        // var utilityAccountNumber = dataa['userValue']['properties']['utility_account_number'];
        const doYouHaveAccount = dataa.userValue.properties.do_you_have_an_account_with_utility_service_provider
        const dealStage = dataa.userValue.properties.dealstage
        void dealStage
        if (isWeBackFromTYP !== true) {
          if (doYouHaveAccount === "Yes") {
            q(".utilityPictureIpiaap3Wrapper .utility").style.display = "none"
            q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "none"
            q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "none"
            qi("#skipThisForNowAndGetInTouch").checked = false
            qi("#iHaveOnlineAccount").checked = true
            q("#have-online-details").style.display = "block"
          } else if (doYouHaveAccount === "No") {
            q(".utilityPictureIpiaap3Wrapper .utility").style.display = "block"
            q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "block"
            q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "block"
            qi("#iHaveOnlineAccount").checked = false
            q("#have-online-details").style.display = "none"
            qi("#skipThisForNowAndGetInTouch").checked = true
          }
        }

        // if(userName != null){ document.querySelector('#UtilityUsername').value = userName; }
        // if(utilityPass != null){ utilityPass = crypt.decrypt(utilityPass); ... }
        // if(utilityAccountNumber != null){ ... }
      }
    } catch (error) {
      console.error("Error:", error)
    }
  }
  async function getAccountNumberByWebhook(token: any, type: any, utility: any, id: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","getAccountNumberByWebhook",[token,type,utility,id],["token","type","utility","id"]);
    // var ContactId = localStorage.getItem("RecordId");
    const uID = storage.get("utility_id")
    // EXTERNAL Hansen/Bayou account-numbers service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getAccountNumbers.php?contact_id=${id}`
    const requestBody = {
      contact_id: id,
    }
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      accountNumberResponse = JSON.stringify(responseData)

      // Check if there is at least one id
      if (responseData.length > 0) {
        if (responseData.length >= 1) {
          const Accdiv = document.createElement("div")
          const h2 = document.createElement("h2")
          h2.textContent = "Please select Account Number"
          Accdiv.appendChild(h2)
          const accul = document.createElement("ul")
          accul.classList.add("accountNumbersUl")
          await getDataFromUANConfigTable()
          let existAccNoLength = 0
          if (
            JSON.stringify(responseData[0].id) == null ||
            JSON.stringify(responseData[0].id) === "" ||
            JSON.stringify(responseData[0].id) === undefined
          ) {
            manualAccountNumber = true
            credNotVerified = false
            q("#have-online-details").style.display = "none"
            q("#haveOnlineAccountMainDiv").style.display = "none"
            q("#SkipThisFornNow").style.display = "block"
          } else {
          }
          for (const item of responseData) {
            let accShow = false
            const accli = document.createElement("li")
            const accspan = document.createElement("span")
            accli.classList.add(`acc${item.id}`) // Add class to li
            accli.style.display = "none"
            accspan.textContent = item.id
            accli.id = item.id
            accli.appendChild(accspan)
            if (item.meters.length >= 1) {
              const div = document.createElement("div")
              div.classList.add("meterWrapper")
              const h4 = document.createElement("h4")
              h4.textContent = "Please select Additional Attributes"
              div.appendChild(h4)
              div.id = `meterId${item.id}`
              const meterul = document.createElement("ul")
              meterul.id = "meterList"

              /* premises div */
              const premisesDiv = document.createElement("div")
              premisesDiv.classList.add("premisesWrapper")
              premisesDiv.style.display = "none"
              const premisesh4 = document.createElement("h4")
              premisesh4.textContent = "Please select Premises Number"
              premisesDiv.appendChild(premisesh4)
              premisesDiv.id = `premisesId${item.id}`
              const premisesul = document.createElement("ul")
              premisesul.id = "premisesList"
              /* premises div */
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
                const meterli = document.createElement("li")
                const premisesli = document.createElement("li")
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  accli.style.display = "block"
                  meterli.id = `${meter.id}`
                  ;(meterli as any).type = meter.type
                  meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                  if (meter.additional_attributes != null) {
                    meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  if (meter.additional_attributes != null) {
                    premisesli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  meterId = `${meter.id}`
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  const resultT = getValidTariff(meter)
                  if (accShow === false) {
                    if (resultT.isMoreThanOneYear === true) {
                      accShow = false
                      accli.style.display = "none" // hide
                    } else {
                      accShow = true
                      accli.style.display = "block" // show
                    }
                  }
                  if (uID !== "XCEL-CO-SF") {
                    if (meter.additional_attributes != null) {
                      div.classList.remove("hidden")
                      if (
                        additionalAttributeValue !== "" &&
                        additionalAttributeValue != null &&
                        additionalAttributeValue !== undefined
                      ) {
                        if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                          div.classList.add("hidden")
                        } else {
                          div.classList.remove("hidden")
                        }
                      } else {
                        div.classList.add("hidden")
                      }
                      additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                    } else {
                      div.classList.add("hidden")
                    }
                  } else {
                    if (resultT.isMoreThanOneYear !== true) {
                      if (meter.additional_attributes != null) {
                        if (
                          additionalAttributeValue !== "" &&
                          additionalAttributeValue != null &&
                          additionalAttributeValue !== undefined
                        ) {
                          if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                            // (original left this branch empty)
                          } else {
                            additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                            premisesId = meter.additional_attributes[additionalAttributeItem]
                            premisesAddress = meter.address.line_1
                            premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                            premisesul.appendChild(premisesli)
                            premisesDiv.appendChild(premisesul)
                            accli.appendChild(premisesDiv)
                          }
                        } else {
                          additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                          premisesId = meter.additional_attributes[additionalAttributeItem]
                          premisesAddress = meter.address.line_1
                          premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                          premisesul.appendChild(premisesli)
                          premisesDiv.appendChild(premisesul)
                          accli.appendChild(premisesDiv)
                        }
                      }
                    }
                  }

                  // meterli.textContent = `${meter.id}`;
                  meterli.textContent = additionalAttributeValue
                  meterul.appendChild(meterli)
                  div.appendChild(meterul)
                  accli.appendChild(div)
                }
              })
            } else {
              // document.querySelector('.Accdiv h2').outerHTML = "There was an issue retrieving your bills. Please use the 'Skip this for now…' option below";
              Accdiv.innerHTML =
                "<h2>There was an issue retrieving your bills. Please use the 'Skip this for now…' option below</h2>"
              q("#SkipThisFornNow").style.display = "block"
            }

            // eslint-disable-next-line no-inner-declarations
            async function checkUAN1(val: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","checkUAN1",[val],["val"]);
              const property = "uan_1_value"
              const value = val
              const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ property, value }),
              })
              const data = await response.json()
              responseFromAPI = JSON.stringify(data)
              if (response.ok) {
                if (data.total === 0 || data.total === undefined) {
                  // (original left this branch empty)
                } else if (data.total === 1) {
                  const dealIdFromDealFound = data.results[0].id
                  const dealIdFromCurrentPage = storage.get("dealId")
                  if (dealIdFromDealFound === dealIdFromCurrentPage) {
                    // (original left this branch empty)
                  } else {
                    ++existAccNoLength
                    accli.style.cursor = "not-allowed"
                    accli.style.pointerEvents = "none"
                    accli.style.color = "#999"
                    accli.style.opacity = "0.5"
                  }
                } else {
                  ++existAccNoLength
                  accli.style.cursor = "not-allowed"
                  accli.style.pointerEvents = "none"
                  accli.style.color = "#999"
                  accli.style.opacity = "0.5"
                }
              }
            }
            // eslint-disable-next-line no-inner-declarations
            async function checkUAN1andUAN2(prop: any, val: any): Promise<any> {
__ea_trace("modules/usage/usage-bayou.ts","checkUAN1andUAN2",[prop,val],["prop","val"]);
              const property = prop
              const value = val
              const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ property, value }),
              })
              const data = await response.json()

              responseFromAPI = JSON.stringify(data)
              return data
            }
            if (uID !== "XCEL-CO-SF") {
              await checkUAN1(item.id)
            } else {
              const un1val = await checkUAN1andUAN2("uan_1_value", item.id)
              const un2val = await checkUAN1andUAN2("uan_2_value", additionalAttributeValue)

              if (
                (un1val.total === 0 || un1val.total === undefined) &&
                (un2val.total === 0 || un2val.total === undefined)
              ) {
              } else if (un1val.total === 1 && un2val.total === 1) {
                const dealIdFromDealFoundUAN1 = un1val.results[0].id
                const dealIdFromDealFoundUAN2 = un2val.results[0].id
                const dealIdFromCurrentPage = storage.get("dealId")
                if (
                  dealIdFromDealFoundUAN1 === dealIdFromCurrentPage &&
                  dealIdFromDealFoundUAN2 === dealIdFromCurrentPage
                ) {
                } else {
                  ++existAccNoLength
                  accli.style.cursor = "not-allowed"
                  accli.style.pointerEvents = "none"
                  accli.style.color = "#999"
                  accli.style.opacity = "0.5"
                }
              } else if (un1val.total >= 1 && un2val.total === 0) {
              } else if (un1val.total === 0 && un2val.total >= 1) {
              } else if (un1val.total >= 1 && un2val.total >= 1) {
                ++existAccNoLength
                accli.style.cursor = "not-allowed"
                accli.style.pointerEvents = "none"
                accli.style.color = "#999"
                accli.style.opacity = "0.5"
              } else {
                ++existAccNoLength
                accli.style.cursor = "not-allowed"
                accli.style.pointerEvents = "none"
                accli.style.color = "#999"
                accli.style.opacity = "0.5"
              }
            }

            accul.appendChild(accli)
            Accdiv.appendChild(accul)
            q(".have-online-details").appendChild(Accdiv)
          }
          closeProgressBar()
          if (existAccNoLength === responseData.length) {
            verifyidenty = "no"
            // alert("Account number already exists. Please try again.");
            passUpdatedOpenLoader()
          }
          qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
            li.style.display = "none"
          })
          function accountNumberSelected(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","accountNumberSelected",[event],["event"]);
            qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
            })
            qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              li.style.display = "none"
            })
            if (uID === "XCEL-CO-SF") {
              qa(".premisesWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                li.style.display = "none"
              })
            }
            const clickedElement = event.target
            firstId = clickedElement.parentElement.id
            bayouAccountNo = firstId
            const meterWrapper = clickedElement.parentElement.querySelector(".meterWrapper")
            let premisesWrapper: any
            if (uID === "XCEL-CO-SF") {
              premisesWrapper = clickedElement.parentElement.querySelector(".premisesWrapper")
            }
            if (!meterWrapper.classList.contains("hidden")) {
              if (uID !== "XCEL-CO-SF") {
                const ulElement = meterWrapper.querySelector("ul")
                const liElements = ulElement.querySelectorAll("li")
                const numberOfLi = liElements.length
                if (numberOfLi === 1) {
                  verifyidenty = "yes"
                  meterWrapper.style.display = "none"
                  liElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    meterId = li.id
                    meterType = li.type
                    if (li.tariffs) {
                      rateClass = li.tariffs[0].tariff
                    }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                  })
                  meterWrapper.style.display = "block"
                  ulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                      })
                      this.style.backgroundColor = "#f18020"
                      meterId = this.id
                      meterType = this.type
                      if (this.tariffs) {
                        rateClass = this.tariffs[0].tariff
                      }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              } else {
                const premisesulElement = premisesWrapper.querySelector("ul")
                const premisesliElements = premisesulElement.querySelectorAll("li")
                const numberOfPreLi = premisesliElements.length
                if (numberOfPreLi === 1) {
                  verifyidenty = "yes"
                  premisesWrapper.style.display = "none"
                  premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    // meterId = li.id;
                    // meterType = li.type;
                    // if(li.tariffs){ rateClass = li.tariffs[0].tariff; }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                    span.style.color = "#425b76"
                  })
                  premisesWrapper.style.display = "block"
                  premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                        span.style.color = "#425b76"
                      })
                      this.style.backgroundColor = "#f18020"
                      this.style.color = "#ffffff"
                      // meterId = this.id;
                      // meterType = this.type;
                      // if(this.tariffs){ rateClass = this.tariffs[0].tariff; }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              }
            } else {
              if (uID === "XCEL-CO-SF") {
                const premisesulElement = premisesWrapper.querySelector("ul")
                const premisesliElements = premisesulElement.querySelectorAll("li")
                const numberOfPreLi = premisesliElements.length
                if (numberOfPreLi === 1) {
                  verifyidenty = "yes"
                  premisesWrapper.style.display = "none"
                  premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    // meterId = li.id;
                    // meterType = li.type;
                    // if(li.tariffs){ rateClass = li.tariffs[0].tariff; }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                    span.style.color = "#425b76"
                  })
                  premisesWrapper.style.display = "block"
                  premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                        span.style.color = "#425b76"
                      })
                      this.style.backgroundColor = "#f18020"
                      this.style.color = "#ffffff"
                      // meterId = this.id;
                      // meterType = this.type;
                      // if(this.tariffs){ rateClass = this.tariffs[0].tariff; }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              } else {
                verifyidenty = "yes"
              }
            }
            clickedElement.style.backgroundColor = "#f18020"
            qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
              span.removeAttribute("data-selected")
            })
            clickedElement.setAttribute("data-selected", "true")
          }
          qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
            span.addEventListener("click", accountNumberSelected)
          })
        }
        bayouToken = token
        bayouTokenType = type
        bayouUtility = utility
        bayouId = id
        bayouAccountNo = firstId
        pageFoundCloseLoader()
        // updateBayouResponseInDeal(token, type, utility, id, firstId);
        await updateProviderInContact()
      } else {
        if (accountNofuncCallCount === 50) {
          manualAccountNumber = true
          credNotVerified = false

          pageFoundCloseLoader()
          closeProgressBar()
          verifyidenty = "no"
          alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
          q("#have-online-details").style.display = "none"
          q("#haveOnlineAccountMainDiv").style.display = "none"
          q("#SkipThisFornNow").style.display = "block"
        } else {
          ++accountNofuncCallCount
          await getAccountNumberByWebhook(token, type, utility, id)
        }
      }
      if (response.status === 500 || response.status === 404) {
        manualAccountNumber = true
        credNotVerified = false
        alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
        q("#haveOnlineAccountMainDiv").style.display = "none"
        q("#SkipThisFornNow").style.display = "block"
        closeProgressBar()
        // pageFoundCloseLoader();
      }
    } catch (error) {
      manualAccountNumber = true
      credNotVerified = false
      alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
      q("#haveOnlineAccountMainDiv").style.display = "none"
      q("#SkipThisFornNow").style.display = "block"
      closeProgressBar()
      // pageFoundCloseLoader();
      console.error("Error calling API:", error)
    }
  }
  async function updateBayouResponseInDeal(token: any, type: any, utility: any, id: any, firstId: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","updateBayouResponseInDeal",[token,type,utility,id,firstId],["token","type","utility","id","firstId"]);
    void token
    void type
    void utility
    void id
    void firstId
    const dealId = storage.get("dealId")
    pageFoundOpenLoader()
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          bayous_utility_id: bayouUtility,
          bayous_type: bayouTokenType,
          bayous_id: bayouId,
          bayous_onboarding_token: bayouToken,
          uan_1_label: "Bayou's Utility Id",
          uan_1_value: bayouAccountNo,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        pageFoundCloseLoader()
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      pageFoundCloseLoader()
      // alert('Server error. Please try again');
      console.error("Error:", error)
    }
  }
  void updateBayouResponseInDeal
  async function updateProviderInContact(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","updateProviderInContact",[],[]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          is_enrolled_for_bayou: "yes",
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      responseFromAPI = await response.json()
      responseFromAPI = JSON.stringify(responseFromAPI)
    } catch (error) {
      console.error("Error:", error)
    }
  }
  async function getTokenAndDateStamp(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","getTokenAndDateStamp",[],[]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        email: email,
      }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const dateStamp = dataa.userValue.properties.date_stamp
      const bToken = dataa.userValue.properties.bayou_onboarding_token
      void bToken
      const bContactId = dataa.userValue.properties.bayou_id
      storage.set("dateStamp", dateStamp)
      storage.rawSet("bayou_contact_id", bContactId)
      // localStorage.setItem("LoggedName", firstName + " " + lastName);
    } catch (error) {
      console.error("Error:", error)
    }
  }
  async function updateTokenAndDateInContact(token: any, date: any, tokenLink: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","updateTokenAndDateInContact",[token,date,tokenLink],["token","date","tokenLink"]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          bayou_onboarding_token: token,
          date_stamp: date,
          bayou_onboarding_link: tokenLink,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      responseFromAPI = await response.json()
      responseFromAPI = JSON.stringify(responseFromAPI)
    } catch (error) {
      console.error("Error:", error)
    }
  }
  function getCurrentDate(): string {
__ea_trace("modules/usage/usage-bayou.ts","getCurrentDate",[],[]);
    const today = new Date()
    const month = String(today.getMonth() + 1).padStart(2, "0") // Months are 0-based
    const day = String(today.getDate()).padStart(2, "0")
    const year = today.getFullYear()
    return `${month}/${day}/${year}`
  }
  async function BayouGetCustomersWhenTokenExpire(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","BayouGetCustomersWhenTokenExpire",[],[]);
    const bayou_reason = storage.rawGet("bayou_reason")
    const bayouContactId = storage.rawGet("bayou_contact_id")
    verifyidenty = "no"
    // EXTERNAL Hansen/Bayou get-contact service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getContactByID.php?contact_id=${bayouContactId}`
    const requestBody = {
      contact_id: bayouContactId,
    }
    let catchdata: any = ""
    void catchdata
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      const onboardingToken = responseData.onboarding_token
      const onboardingLink = responseData.onboarding_link
      storage.set("onboarding_token", onboardingToken)
      storage.set("dateStamp", getCurrentDate())
      await updateTokenAndDateInContact(onboardingToken, getCurrentDate(), onboardingLink)
      Bayou.loadOnboardingForm(
        q("#have-online-details"),
        60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
        onboardingToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
        (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
          q("#verification-error-message").innerHTML = ""
          if (event.type === "customerHasAuthenticated") {
            credNotVerified = false
            manualAccountNumber = false
            mfaRequired = "No"
            q("#haveOnlineAccountMainDiv").style.display = "block"
            q("#SkipThisFornNow").style.display = "none"
            storage.rawSet("BayouContactId", event.data.id)
            openProgressBar()
            // alert("Customer has completed the form");
            setTimeout(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
              getAccountNumberByWebhook(onboardingToken, event.type, event.data.utility, event.data.id)
            }, 10000)
          } else if (event.type === "customerEnteredInvalidCredentials") {
            credNotVerified = true
            manualAccountNumber = true
            q("#haveOnlineAccountMainDiv").style.display = "none"
            mfaRequired = "No"
            q("#SkipThisFornNow").style.display = "block"
            alert("Customer entered invalid credentials")
            verifyidenty = "no"
          } else if (event.type === "customerEnteredInvalidMFAChallenge") {
            credNotVerified = true
            manualAccountNumber = true
            mfaRequired = "Yes"
            q("#haveOnlineAccountMainDiv").style.display = "none"
            q("#SkipThisFornNow").style.display = "block"
            alert("Customer entered invalid MFA challenge")
            verifyidenty = "no"
          }
        },
      )
    } catch (error) {
      verifyidenty = "no"
      pageFoundCloseLoader()
      console.error("Error calling API:", error)
    }
  }
  async function BayouCreateCustomer(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","BayouCreateCustomer",[],[]);
    const UtilitySessionDetails = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    const ContactId = storage.get("RecordId")
    verifyidenty = "no"
    const dealId = storage.get("dealId")
    // EXTERNAL Hansen/Bayou create-customer service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = "https://hansen.danmarcrm.com/bayouCreateCustomer.php"
    const requestBody = {
      utility: UtilitySessionDetails["9"],
      external_id: ContactId,
      deal_id: dealId,
    }
    const onbToken: any = storage.get("onboarding_token")
    if (onbToken == null || onbToken === "null" || onbToken === undefined || onbToken === "undefined") {
      let catchdata: any = ""
      try {
        const response = await fetch(hensonApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        })
        catchdata = requestBody
        const responseData = await response.text()
        responseFromAPI = JSON.stringify(responseData)

        if (typeof Sentry !== "undefined" && Sentry.captureMessage) {
          Sentry.captureMessage("API Response", {
            level: "info",
            tags: {
              api_endpoint: "bayouCreateCustomer",
              api_method: "POST",
              api_status: response.status,
              api_ok: response.ok,
            },
            extra: {
              requestBody: requestBody,
              responseData: responseData,
            },
          })
        }

        // Log to Nautilus API
        try {
          fetch(apiUrl("LogToNautilus"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: "https://dev2hansen.danmarcrm.com/bayouCreateCustomer.php",
              request_method: "POST",
              request_body: requestBody,
              response: {
                status: response.status,
                ok: response.ok,
                data: responseData,
              },
              metadata: {
                module_name: "Page_2_My_Usage_Bayou",
                api_endpoint: "bayouCreateCustomer",
                timestamp: new Date().toISOString(),
              },
            }),
          }).catch((e: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[e],["e"]);
            console.error("Failed to log to Nautilus:", e.message)
          })
        } catch (e) {
          console.error("Nautilus logging error:", e)
        }

        const onboardingTokenMatch = responseData.match(/"onboarding_token"\s*:\s*"([^"]+)"/)
        const onboardingLinkMatch = responseData.match(/"onboarding_link"\s*:\s*"([^"]+)"/)
        const onboardingLink = onboardingLinkMatch ? onboardingLinkMatch[1] : null

        if (onboardingTokenMatch?.[1]) {
          const onboardingToken = onboardingTokenMatch[1]
          bayouToken = onboardingTokenMatch[1]
          storage.set("onboarding_token", onboardingToken)
          Bayou.loadOnboardingForm(
            q("#have-online-details"),
            60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
            onboardingToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
            (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
              q("#verification-error-message").innerHTML = ""
              if (event.type === "customerHasAuthenticated") {
                credNotVerified = false
                manualAccountNumber = false
                mfaRequired = "No"
                q("#haveOnlineAccountMainDiv").style.display = "block"
                q("#SkipThisFornNow").style.display = "none"
                storage.rawSet("BayouContactId", event.data.id)
                openProgressBar()
                // alert("Customer has completed the form");
                setTimeout(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
                  getAccountNumberByWebhook(onboardingToken, event.type, event.data.utility, event.data.id)
                }, 10000)
              } else if (event.type === "customerEnteredInvalidCredentials") {
                credNotVerified = true
                manualAccountNumber = true
                q("#haveOnlineAccountMainDiv").style.display = "none"
                mfaRequired = "No"
                q("#SkipThisFornNow").style.display = "block"
                alert("Customer entered invalid credentials")
                verifyidenty = "no"
              } else if (event.type === "customerEnteredInvalidMFAChallenge") {
                credNotVerified = true
                manualAccountNumber = true
                mfaRequired = "Yes"
                q("#haveOnlineAccountMainDiv").style.display = "none"
                q("#SkipThisFornNow").style.display = "block"
                alert("Customer entered invalid MFA challenge")
                verifyidenty = "no"
              }
            },
          )
          updateTokenAndDateInContact(onboardingToken, getCurrentDate(), onboardingLink)
          pageFoundCloseLoader()
        } else {
          credNotVerified = true
          manualAccountNumber = true
          q("#haveOnlineAccountMainDiv").style.display = "none"
          q("#SkipThisFornNow").style.display = "block"
          console.error("onboarding_token not found in the response")
          verifyidenty = "no"
          pageFoundCloseLoader()
        }
        if (response.status === 500 || response.status === 404) {
          credNotVerified = true
          manualAccountNumber = true
          q("#haveOnlineAccountMainDiv").style.display = "none"
          q("#SkipThisFornNow").style.display = "block"
          verifyidenty = "no"
          pageFoundCloseLoader()
        }
      } catch (error) {
        credNotVerified = true
        manualAccountNumber = true
        q("#haveOnlineAccountMainDiv").style.display = "none"
        q("#SkipThisFornNow").style.display = "block"
        verifyidenty = "no"
        pageFoundCloseLoader()
        console.error("Error calling API:", error)
      }
    } else {
      await getTokenAndDateStamp()
      // eslint-disable-next-line no-inner-declarations
      function isNineDaysPassed(tokenCreatedDate: any): boolean {
__ea_trace("modules/usage/usage-bayou.ts","isNineDaysPassed",[tokenCreatedDate],["tokenCreatedDate"]);
        const created: any = new Date(tokenCreatedDate)
        const today: any = new Date()

        // Calculate difference in milliseconds
        const diffInMs = today - created

        // Convert to days
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

        return diffInDays >= 9
      }

      const tokenCreatedDate = storage.get("dateStamp") // e.g., "04/28/2025"
      if (tokenCreatedDate && isNineDaysPassed(tokenCreatedDate)) {
        await BayouGetCustomersWhenTokenExpire()
      } else {
        Bayou.loadOnboardingForm(
          q("#have-online-details"),
          60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
          onbToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
          (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
            q("#verification-error-message").innerHTML = ""
            if (event.type === "customerHasAuthenticated") {
              credNotVerified = false
              manualAccountNumber = false
              mfaRequired = "No"
              q("#haveOnlineAccountMainDiv").style.display = "block"
              q("#SkipThisFornNow").style.display = "none"
              openProgressBar()
              // alert("Customer has completed the form");
              storage.rawSet("BayouContactId", event.data.id)
              // verifyidenty = 'yes';
              setTimeout(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
                getAccountNumberByWebhook(onbToken, event.type, event.data.utility, event.data.id)
              }, 5000)
            } else if (event.type === "customerEnteredInvalidCredentials") {
              credNotVerified = true
              manualAccountNumber = true
              q("#haveOnlineAccountMainDiv").style.display = "none"
              mfaRequired = "No"
              q("#SkipThisFornNow").style.display = "block"
              alert("Customer entered invalid credentials")
              verifyidenty = "no"
            } else if (event.type === "customerEnteredInvalidMFAChallenge") {
              credNotVerified = true
              manualAccountNumber = true
              q("#haveOnlineAccountMainDiv").style.display = "none"
              mfaRequired = "Yes"
              q("#SkipThisFornNow").style.display = "block"
              alert("Customer entered invalid MFA challenge")
              verifyidenty = "no"
            }
          },
        )
      }
      pageFoundCloseLoader()
    }
  }
  /* --------------- for first enrollment ----------------------- */

  /* --------------- for multi enrollment ----------------------- */
  async function GetDataFromDealRecordMulti(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","GetDataFromDealRecordMulti",[],[]);
    pageFoundOpenLoader()
    await BayouGetCustomers()
    // var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
      } else if (dataa.message === "User Found") {
        const doYouHaveAccount = dataa.userValue.properties.do_you_have_an_account_with_utility_service_provider
        const dealStage = dataa.userValue.properties.dealstage
        void dealStage
        if (isWeBackFromTYP !== true) {
          if (doYouHaveAccount === "Yes") {
            q(".utilityPictureIpiaap3Wrapper .utility").style.display = "none"
            q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "none"
            q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "none"
            qi("#skipThisForNowAndGetInTouch").checked = false
            qi("#iHaveOnlineAccount").checked = true
            q("#have-online-details").style.display = "block"
          } else if (doYouHaveAccount === "No") {
            q(".utilityPictureIpiaap3Wrapper .utility").style.display = "block"
            q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "block"
            q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "block"
            qi("#iHaveOnlineAccount").checked = false
            q("#have-online-details").style.display = "none"
            qi("#skipThisForNowAndGetInTouch").checked = true
          }
        }

        // if(userName != null){ ... } etc (kept commented in original)
      }
    } catch (error) {
      console.error("Error:", error)
    }
  }
  async function BayouGetCustomers(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","BayouGetCustomers",[],[]);
    const bayou_reason = storage.rawGet("bayou_reason")
    const UtilitySessionDetails = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    void UtilitySessionDetails
    const bayouContactId = storage.rawGet("bayou_contact_id")
    // verifyidenty = 'no';
    const dealId = storage.get("dealId")
    void dealId
    // EXTERNAL Hansen/Bayou get-contact service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getContactByID.php?contact_id=${bayouContactId}`
    const requestBody = {
      contact_id: bayouContactId,
    }
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      if (bayou_reason == null || bayou_reason === undefined || bayou_reason === "" || "null") {
        const accountNumbers = responseData.account_numbers.map((account: any) => account.id)
        const accountDetails = responseData.account_numbers.map((account: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[account],["account"]);
          return {
            account_number: account.id,
            meters: account.meters.map((meter: any) => ({
              id: meter.id,
              type: meter.type,
              tariffs: meter.tariffs,
              address: meter.address,
              additional_attributes: meter.additional_attributes,
            })),
          }
        })
        if (accountDetails.length > 0) {
          if (accountDetails.length === 1) {
            // findsingleAccountNumber(localStorage.getItem("RecordId"), accountDetails[0].account_number, accountDetails);
            findmultiAccountNumber(storage.get("RecordId"), accountDetails, accountDetails)
          } else if (accountDetails.length > 1) {
            findmultiAccountNumber(storage.get("RecordId"), accountDetails, accountDetails)
          }
        } else {
          manualAccountNumber = true
          credNotVerified = false
          alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
          q("#haveOnlineAccountMainDiv").style.display = "none"
          q("#SkipThisFornNow").style.display = "block"
          verifyidenty = "no"
        }
        pageFoundCloseLoader()
      } else {
        await getTokenAndDateStamp()
        // eslint-disable-next-line no-inner-declarations
        function isNineDaysPassed(tokenCreatedDate: any): boolean {
__ea_trace("modules/usage/usage-bayou.ts","isNineDaysPassed",[tokenCreatedDate],["tokenCreatedDate"]);
          const created: any = new Date(tokenCreatedDate)
          const today: any = new Date()

          // Calculate difference in milliseconds
          const diffInMs = today - created

          // Convert to days
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

          return diffInDays >= 9
        }

        const tokenCreatedDate = storage.get("dateStamp") // e.g., "04/28/2025"
        if (tokenCreatedDate && isNineDaysPassed(tokenCreatedDate)) {
          await BayouGetCustomersWhenTokenExpire()
        } else {
          const onbToken = storage.get("onboarding_token")
          Bayou.loadOnboardingForm(
            q("#have-online-details"),
            60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
            onbToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
            (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
              q("#verification-error-message").innerHTML = ""
              if (event.type === "customerHasAuthenticated") {
                credNotVerified = false
                manualAccountNumber = false
                mfaRequired = "No"
                q("#haveOnlineAccountMainDiv").style.display = "block"
                q("#SkipThisFornNow").style.display = "none"
                openProgressBar()
                // alert("Customer has completed the form");
                storage.rawSet("BayouContactId", event.data.id)
                // verifyidenty = 'yes';
                setTimeout(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
                  getAccountNumberByWebhook(onbToken, event.type, event.data.utility, event.data.id)
                }, 5000)
              } else if (event.type === "customerEnteredInvalidCredentials") {
                credNotVerified = true
                manualAccountNumber = true
                q("#haveOnlineAccountMainDiv").style.display = "none"
                q("#SkipThisFornNow").style.display = "block"
                mfaRequired = "No"
                alert("Customer entered invalid credentials")
                verifyidenty = "no"
              } else if (event.type === "customerEnteredInvalidMFAChallenge") {
                credNotVerified = true
                manualAccountNumber = true
                mfaRequired = "Yes"
                q("#haveOnlineAccountMainDiv").style.display = "none"
                q("#SkipThisFornNow").style.display = "block"
                alert("Customer entered invalid MFA challenge")
                verifyidenty = "no"
              }
              // else{ document.querySelector('#SkipThisFornNow').style.display = "block";
              //   alert("Invalid Credentials"); verifyidenty = 'no'; }
            },
          )
        }
        pageFoundCloseLoader()
      }
      if (response.status === 500 || response.status === 404) {
        manualAccountNumber = true
        credNotVerified = true
        alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
        q("#haveOnlineAccountMainDiv").style.display = "none"
        q("#SkipThisFornNow").style.display = "block"
        verifyidenty = "no"
        pageFoundCloseLoader()
      }
    } catch (error) {
      manualAccountNumber = true
      credNotVerified = false
      alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
      q("#haveOnlineAccountMainDiv").style.display = "none"
      q("#SkipThisFornNow").style.display = "block"
      verifyidenty = "no"
      pageFoundCloseLoader()
      console.error("Error calling API:", error)
    }
  }
  async function findsingleAccountNumber(contactId: any, accountnumber: any, responseData: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","findsingleAccountNumber",[contactId,accountnumber,responseData],["contactId","accountnumber","responseData"]);
    void contactId
    const property = "uan_1_value"
    const value = accountnumber
    const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ property, value }),
    })
    const data = await response.json()
    responseFromAPI = JSON.stringify(data)
    if (response.ok) {
      if (data.total === 0 || data.total === undefined) {
        q("#SkipThisFornNow").style.display = "none"
        await getDataFromUANConfigTable()
        // alert("Account number not exists.");
        firstId = accountnumber
        bayouAccountNo = firstId
        responseData.forEach((item: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[item],["item"]);
          if (item.meters.length === 1) {
            item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
              verifyidenty = "yes"
              let mtType = meter.type
              mtType = mtType.toUpperCase()
              if (mtType === "ELECTRIC") {
                meterId = meter.id
                meterType = meter.type
                rateClass = meter.tariffs[0].tariff
                if (meter.additional_attributes != null) {
                  additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                }
              } else {
                meterId = ""
                meterType = ""
                rateClass = ""
                additionalAttributeValue = ""
              }
            })
          } else if (item.meters.length > 1) {
            let ifelectricMetermorethan1 = 0
            const div = document.createElement("div")
            div.classList.add("meterWrapper")
            const h4 = document.createElement("h4")
            h4.textContent = "Please select Additional Attributes"
            div.appendChild(h4)
            const meterul = document.createElement("ul")
            meterul.id = "meterList"
            item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
              const meterli = document.createElement("li")
              let mtType = meter.type
              mtType = mtType.toUpperCase()
              if (mtType === "ELECTRIC") {
                meterli.id = `${meter.id}`
                ;(meterli as any).type = meter.type
                meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                if (meter.additional_attributes != null) {
                  meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                }
                ifelectricMetermorethan1++
                void ifelectricMetermorethan1
                meterId = `${meter.id}`
                meterType = meter.type
                rateClass = meter.tariffs[0].tariff
                if (meter.additional_attributes != null) {
                  div.classList.remove("hidden")
                  if (
                    additionalAttributeValue !== "" &&
                    additionalAttributeValue != null &&
                    additionalAttributeValue !== undefined
                  ) {
                    if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                      div.classList.add("hidden")
                    } else {
                      div.classList.remove("hidden")
                    }
                  } else {
                    div.classList.add("hidden")
                  }
                  additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                } else {
                  div.classList.add("hidden")
                }
                // meterli.textContent = `${meter.id}`;
                meterli.textContent = additionalAttributeValue
                meterul.appendChild(meterli)
              }
            })
            const meterWrapper = q(".meterWrapper")
            if (!meterWrapper.classList.contains("hidden")) {
              div.appendChild(meterul)
              q(".have-online-details").appendChild(div)
              // eslint-disable-next-line no-inner-declarations
              function handleClick(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","handleClick",[event],["event"]);
                verifyidenty = "yes"
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                  li.style.backgroundColor = "#ffffff"
                })
                const clickedElement = event.target
                meterId = clickedElement.id
                meterType = clickedElement.type
                rateClass = clickedElement.tariff
                additionalAttributeValue = clickedElement.getAttribute("aa")
                clickedElement.style.backgroundColor = "#f18020"
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                  li.removeAttribute("data-selected")
                })
                clickedElement.setAttribute("data-selected", "true")
              }
              qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                li.addEventListener("click", handleClick)
              })
            } else {
              verifyidenty = "yes"
            }
          }
        })
      } else if (data.total === 1) {
        const dealIdFromDealFound = data.results[0].id
        const dealIdFromCurrentPage = storage.get("dealId")
        if (dealIdFromDealFound === dealIdFromCurrentPage) {
          q("#SkipThisFornNow").style.display = "none"
          await getDataFromUANConfigTable()
          // alert("Account number not exists.");
          firstId = accountnumber
          bayouAccountNo = firstId
          responseData.forEach((item: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[item],["item"]);
            if (item.meters.length === 1) {
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
                verifyidenty = "yes"
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  meterId = meter.id
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  if (meter.additional_attributes != null) {
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                  }
                } else {
                  meterId = ""
                  meterType = ""
                  rateClass = ""
                  additionalAttributeValue = ""
                }
              })
            } else if (item.meters.length > 1) {
              let ifelectricMetermorethan1 = 0
              const div = document.createElement("div")
              div.classList.add("meterWrapper")
              const h4 = document.createElement("h4")
              h4.textContent = "Please select Additional Attributes"
              div.appendChild(h4)
              const meterul = document.createElement("ul")
              meterul.id = "meterList"
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
                const meterli = document.createElement("li")
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  meterli.id = `${meter.id}`
                  ;(meterli as any).type = meter.type
                  meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                  if (meter.additional_attributes != null) {
                    meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  ifelectricMetermorethan1++
                  void ifelectricMetermorethan1
                  meterId = `${meter.id}`
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  if (meter.additional_attributes != null) {
                    div.classList.remove("hidden")
                    if (
                      additionalAttributeValue !== "" &&
                      additionalAttributeValue != null &&
                      additionalAttributeValue !== undefined
                    ) {
                      if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                        div.classList.add("hidden")
                      } else {
                        div.classList.remove("hidden")
                      }
                    } else {
                      div.classList.add("hidden")
                    }
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                  } else {
                    div.classList.add("hidden")
                  }
                  // meterli.textContent = `${meter.id}`;
                  meterli.textContent = additionalAttributeValue
                  meterul.appendChild(meterli)
                }
              })
              const meterWrapper = q(".meterWrapper")
              if (!meterWrapper.classList.contains("hidden")) {
                div.appendChild(meterul)
                q(".have-online-details").appendChild(div)
                // eslint-disable-next-line no-inner-declarations
                function handleClick(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","handleClick",[event],["event"]);
                  verifyidenty = "yes"
                  qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    li.style.backgroundColor = "#ffffff"
                  })
                  const clickedElement = event.target
                  meterId = clickedElement.id
                  meterType = clickedElement.type
                  rateClass = clickedElement.tariff
                  additionalAttributeValue = clickedElement.getAttribute("aa")
                  clickedElement.style.backgroundColor = "#f18020"
                  qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                    li.removeAttribute("data-selected")
                  })
                  clickedElement.setAttribute("data-selected", "true")
                }
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
                  li.addEventListener("click", handleClick)
                })
              } else {
                verifyidenty = "yes"
              }
            }
          })
        } else {
          verifyidenty = "no"
          // alert("Account number already exists. Please try again.");
          passUpdatedOpenLoader()
        }
      } else {
        q("#SkipThisFornNow").style.display = "block"
        verifyidenty = "no"
        // alert("Account number already exists. Please try again.");
        passUpdatedOpenLoader()
        // return false;
      }
    }
  }
  void findsingleAccountNumber
  async function findmultiAccountNumber(contactId: any, accountnumber: any, responseData: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","findmultiAccountNumber",[contactId,accountnumber,responseData],["contactId","accountnumber","responseData"]);
    void contactId
    void accountnumber
    const uID = storage.get("utility_id")
    await getDataFromUANConfigTable()
    const Accdiv = document.createElement("div")
    const h2 = document.createElement("h2")
    h2.textContent = "Please select Account Number"
    Accdiv.appendChild(h2)
    const accul = document.createElement("ul")
    accul.classList.add("accountNumbersUl")
    let existAccNoLength = 0
    if (
      JSON.stringify(responseData[0].account_number) == null ||
      JSON.stringify(responseData[0].account_number) === "" ||
      JSON.stringify(responseData[0].account_number) === undefined
    ) {
      manualAccountNumber = true
      credNotVerified = false
      q("#have-online-details").style.display = "none"
      alert("We validated your utility login, but are unable to retrieve account details. Please use Skip below…")
      q("#haveOnlineAccountMainDiv").style.display = "none"
      q("#SkipThisFornNow").style.display = "block"
    }
    for (const item of responseData) {
      let accShow = false
      const accli = document.createElement("li")
      const accspan = document.createElement("span")
      accli.classList.add(`acc${item.account_number}`) // Add class to li
      accli.style.display = "none"
      accspan.textContent = item.account_number
      accli.id = item.account_number
      accli.appendChild(accspan)
      if (item.meters.length >= 1) {
        const div = document.createElement("div")
        div.classList.add("meterWrapper")
        const h4 = document.createElement("h4")
        h4.textContent = "Please select Additional Attributes"
        div.appendChild(h4)
        div.id = `meterId${item.account_number}`
        const meterul = document.createElement("ul")
        meterul.id = "meterList"

        /* premises div */
        const premisesDiv = document.createElement("div")
        premisesDiv.classList.add("premisesWrapper")
        premisesDiv.style.display = "none"
        const premisesh4 = document.createElement("h4")
        premisesh4.textContent = "Please select Premises Number"
        premisesDiv.appendChild(premisesh4)
        premisesDiv.id = `premisesId${item.account_number}`
        const premisesul = document.createElement("ul")
        premisesul.id = "premisesList"
        /* premises div */
        item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[meter],["meter"]);
          const meterli = document.createElement("li")
          const premisesli = document.createElement("li")
          let mtType = meter.type
          mtType = mtType.toUpperCase()
          if (mtType === "ELECTRIC") {
            accli.style.display = "block"
            meterli.id = `${meter.id}`
            ;(meterli as any).type = meter.type
            meterli.setAttribute("tariff", meter.tariffs[0].tariff)
            if (meter.additional_attributes != null) {
              meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
            }
            if (meter.additional_attributes != null) {
              premisesli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
            }
            meterId = `${meter.id}`
            meterType = meter.type
            rateClass = meter.tariffs[0].tariff
            const resultT = getValidTariff(meter)
            if (accShow === false) {
              if (resultT.isMoreThanOneYear === true) {
                accShow = false
                accli.style.display = "none" // hide
              } else {
                accShow = true
                accli.style.display = "block" // show
              }
            }
            if (uID !== "XCEL-CO-SF") {
              if (meter.additional_attributes != null) {
                div.classList.remove("hidden")
                if (
                  additionalAttributeValue !== "" &&
                  additionalAttributeValue != null &&
                  additionalAttributeValue !== undefined
                ) {
                  if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                    div.classList.add("hidden")
                  } else {
                    div.classList.remove("hidden")
                  }
                } else {
                  div.classList.add("hidden")
                }
                additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
              } else {
                div.classList.add("hidden")
              }
            } else {
              if (resultT.isMoreThanOneYear !== true) {
                if (meter.additional_attributes != null) {
                  if (
                    additionalAttributeValue !== "" &&
                    additionalAttributeValue != null &&
                    additionalAttributeValue !== undefined
                  ) {
                    if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                      // (original left this branch empty)
                    } else {
                      additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                      premisesId = meter.additional_attributes[additionalAttributeItem]
                      premisesAddress = meter.address.line_1
                      premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                      premisesul.appendChild(premisesli)
                      premisesDiv.appendChild(premisesul)
                      accli.appendChild(premisesDiv)
                    }
                  } else {
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                    premisesId = meter.additional_attributes[additionalAttributeItem]
                    premisesAddress = meter.address.line_1
                    premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                    premisesul.appendChild(premisesli)
                    premisesDiv.appendChild(premisesul)
                    accli.appendChild(premisesDiv)
                  }
                }
              }
            }

            // meterli.textContent = `${meter.id}`;
            meterli.textContent = additionalAttributeValue
            meterul.appendChild(meterli)
            div.appendChild(meterul)
            accli.appendChild(div)
          }
        })
      } else {
        Accdiv.innerHTML =
          "<h2>There was an issue retrieving your bills. Please use the 'Skip this for now…' option below</h2>"
        q("#SkipThisFornNow").style.display = "block"
      }

      // eslint-disable-next-line no-inner-declarations
      async function checkUAN1(val: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","checkUAN1",[val],["val"]);
        const property = "uan_1_value"
        const value = val
        const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ property, value }),
        })
        const data = await response.json()
        responseFromAPI = JSON.stringify(data)
        if (response.ok) {
          if (data.total === 0 || data.total === undefined) {
            // (original left this branch empty)
          } else if (data.total === 1) {
            const dealIdFromDealFound = data.results[0].id
            const dealIdFromCurrentPage = storage.get("dealId")
            if (dealIdFromDealFound === dealIdFromCurrentPage) {
              // (original left this branch empty)
            } else {
              ++existAccNoLength
              accli.style.cursor = "not-allowed"
              accli.style.pointerEvents = "none"
              accli.style.color = "#999"
              accli.style.opacity = "0.5"
            }
          } else {
            ++existAccNoLength
            accli.style.cursor = "not-allowed"
            accli.style.pointerEvents = "none"
            accli.style.color = "#999"
            accli.style.opacity = "0.5"
          }
        }
      }
      // eslint-disable-next-line no-inner-declarations
      async function checkUAN1andUAN2(prop: any, val: any): Promise<any> {
__ea_trace("modules/usage/usage-bayou.ts","checkUAN1andUAN2",[prop,val],["prop","val"]);
        const property = prop
        const value = val
        const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ property, value }),
        })
        const data = await response.json()

        responseFromAPI = JSON.stringify(data)
        return data
      }
      if (uID !== "XCEL-CO-SF") {
        await checkUAN1(item.account_number)
      } else {
        const un1val = await checkUAN1andUAN2("uan_1_value", item.account_number)
        const un2val = await checkUAN1andUAN2("uan_2_value", additionalAttributeValue)

        if ((un1val.total === 0 || un1val.total === undefined) && (un2val.total === 0 || un2val.total === undefined)) {
        } else if (un1val.total === 1 && un2val.total === 1) {
          const dealIdFromDealFoundUAN1 = un1val.results[0].id
          const dealIdFromDealFoundUAN2 = un2val.results[0].id
          const dealIdFromCurrentPage = storage.get("dealId")
          if (dealIdFromDealFoundUAN1 === dealIdFromCurrentPage && dealIdFromDealFoundUAN2 === dealIdFromCurrentPage) {
          } else {
            ++existAccNoLength
            accli.style.cursor = "not-allowed"
            accli.style.pointerEvents = "none"
            accli.style.color = "#999"
            accli.style.opacity = "0.5"
          }
        } else if (un1val.total >= 1 && un2val.total === 0) {
        } else if (un1val.total === 0 && un2val.total >= 1) {
        } else if (un1val.total >= 1 && un2val.total >= 1) {
          ++existAccNoLength
          accli.style.cursor = "not-allowed"
          accli.style.pointerEvents = "none"
          accli.style.color = "#999"
          accli.style.opacity = "0.5"
        } else {
          ++existAccNoLength
          accli.style.cursor = "not-allowed"
          accli.style.pointerEvents = "none"
          accli.style.color = "#999"
          accli.style.opacity = "0.5"
        }
      }

      accul.appendChild(accli)
      Accdiv.appendChild(accul)
      q(".have-online-details").appendChild(Accdiv)
    }
    if (existAccNoLength === responseData.length) {
      verifyidenty = "no"
      q("#haveOnlineAccountMainDiv").style.display = "none"
      q("#SkipThisFornNow").style.display = "block"
      // alert("Account number already exists. Please try again.");
      passUpdatedOpenLoader()
    }
    qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
      li.style.display = "none"
    })
    function accountNumberSelected(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","accountNumberSelected",[event],["event"]);
      qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
        span.style.backgroundColor = "#ffffff"
      })
      qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
        li.style.display = "none"
      })
      if (uID === "XCEL-CO-SF") {
        qa(".premisesWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
          li.style.display = "none"
        })
      }
      const clickedElement = event.target
      firstId = clickedElement.parentElement.id
      bayouAccountNo = firstId
      const meterWrapper = clickedElement.parentElement.querySelector(".meterWrapper")
      let premisesWrapper: any
      if (uID === "XCEL-CO-SF") {
        premisesWrapper = clickedElement.parentElement.querySelector(".premisesWrapper")
      }
      if (!meterWrapper.classList.contains("hidden")) {
        if (uID !== "XCEL-CO-SF") {
          const ulElement = meterWrapper.querySelector("ul")
          const liElements = ulElement.querySelectorAll("li")
          const numberOfLi = liElements.length
          if (numberOfLi === 1) {
            verifyidenty = "yes"
            meterWrapper.style.display = "none"
            liElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              meterId = li.id
              meterType = li.type
              if (li.tariffs) {
                rateClass = li.tariffs[0].tariff
              }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
            })
            meterWrapper.style.display = "block"
            ulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                })
                this.style.backgroundColor = "#f18020"
                meterId = this.id
                meterType = this.type
                if (this.tariffs) {
                  rateClass = this.tariffs[0].tariff
                }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        } else {
          const premisesulElement = premisesWrapper.querySelector("ul")
          const premisesliElements = premisesulElement.querySelectorAll("li")
          const numberOfPreLi = premisesliElements.length
          if (numberOfPreLi === 1) {
            verifyidenty = "yes"
            premisesWrapper.style.display = "none"
            premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              // meterId = li.id;
              // meterType = li.type;
              // if(li.tariffs){ rateClass = li.tariffs[0].tariff; }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
              span.style.color = "#425b76"
            })
            premisesWrapper.style.display = "block"
            premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                  span.style.color = "#425b76"
                })
                this.style.backgroundColor = "#f18020"
                this.style.color = "#ffffff"
                // meterId = this.id;
                // meterType = this.type;
                // if(this.tariffs){ rateClass = this.tariffs[0].tariff; }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        }
      } else {
        if (uID === "XCEL-CO-SF") {
          const premisesulElement = premisesWrapper.querySelector("ul")
          const premisesliElements = premisesulElement.querySelectorAll("li")
          const numberOfPreLi = premisesliElements.length
          if (numberOfPreLi === 1) {
            verifyidenty = "yes"
            premisesWrapper.style.display = "none"
            premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              // meterId = li.id;
              // meterType = li.type;
              // if(li.tariffs){ rateClass = li.tariffs[0].tariff; }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
              span.style.color = "#425b76"
            })
            premisesWrapper.style.display = "block"
            premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                  span.style.color = "#425b76"
                })
                this.style.backgroundColor = "#f18020"
                this.style.color = "#ffffff"
                // meterId = this.id;
                // meterType = this.type;
                // if(this.tariffs){ rateClass = this.tariffs[0].tariff; }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        } else {
          verifyidenty = "yes"
        }
      }
      clickedElement.style.backgroundColor = "#f18020"
      qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
        span.removeAttribute("data-selected")
      })
      clickedElement.setAttribute("data-selected", "true")
    }
    qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[span],["span"]);
      span.addEventListener("click", accountNumberSelected)
    })
  }
  /* --------------- for multi enrollment ----------------------- */

  // --- Go back personal information button ---------------------------------
  dq(".page1lmileftright_myusage #goback-personal-info").addEventListener("click", () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
    window.location.href = "/my-info"
  })
  // --- Go back personal information button ---------------------------------

  // If user is not logedIn then redirected them to login page
  function getEmail(): boolean | undefined {
__ea_trace("modules/usage/usage-bayou.ts","getEmail",[],[]);
    const storedEmail = storage.get("userEmail")
    if (!storedEmail) {
      window.location.href = "/"
      return false
    }
  }
  getEmail()
  // If user is not logedIn then redirected them to login page
  function triggerFilePicker(): void {
__ea_trace("modules/usage/usage-bayou.ts","triggerFilePicker",[],[]);
    // document.querySelector('#mobileCamera input[type="file"]').click();
    q("#file-error-message").innerHTML = ""
    q("#fileInput").click()
  }
  q("#fileInput").addEventListener("change", (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    handleFilesNew(event.target.files)
  })

  function ShowHide(): void {
__ea_trace("modules/usage/usage-bayou.ts","ShowHide",[],[]);
    toggleElementVisibility("haveOnlineAccountMainDiv", "UTILITY CREDENTIALS DISPLAY")
    // toggleElementVisibility("iDoNotHaveOnlineAcc", "CREATE UTILITY ACCOUNT DISPLAY");
    toggleElementVisibility("SkipThisFornNow", "SKIP FOR NOW DISPLAY")
    // toggleElementVisibility("utility", "UTILITY ACCOUNT NUMBER DISPLAY");
    // toggleElementVisibility("upload-picture-main", "BILL UPLOAD DISPLAY");
  }
  function toggleElementVisibility(elementId: string, nameFromTable: string): void {
__ea_trace("modules/usage/usage-bayou.ts","toggleElementVisibility",[elementId,nameFromTable],["elementId","nameFromTable"]);
    const element = q(`#${elementId}`)

    if (storedDisplayElementList.includes(nameFromTable)) {
      element.style.display = "block"
    } else {
      element.style.display = "none"
    }
  }
  ShowHide()

  function SetRequire(): void {
__ea_trace("modules/usage/usage-bayou.ts","SetRequire",[],[]);
    // UTILITY CREDENTIALS REQUIRED / CREATE UTILITY ACCOUNT REQUIRED /
    // SKIP FOR NOW REQUIRED / UTILITY ACCOUNT NUMBER REQUIRED / BILL UPLOAD REQUIRED
    if (storedRequireElementList.includes("UTILITY CREDENTIALS REQUIRED")) {
      // (original left this branch empty)
    }
  }
  void SetRequire

  // get data from UAN config table ----------------------------------------------------
  async function getallDataFromUANConfigTable(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","getallDataFromUANConfigTable",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const stateForUANConfig = storage.get("stateForLMIConfig")
    const utilityNameForUANConfig = storage.get("utility_Name")
    const hubDBEndpointUANC = hubdbRowsUrl("uanConfig", {
      orderBy: "uan_field_display_sequence",
      utility_id: String(exampleUtilityId),
      state_name: String(stateForUANConfig),
    })
    const responseUANC = await fetch(hubDBEndpointUANC, { method: "GET" })
    const dataUANC = await responseUANC.json()
    const rowsUANC = dataUANC.objects
    if (rowsUANC.length >= 1) {
      rowsUANC.forEach((element: any, index: number) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[element,index],["element","index"]);
        const uanFieldDisplay = element.values["4"].name
        if (uanFieldDisplay === "Yes") {
          uanFieldDefault[index] = element.values["5"]
          uanFieldDisplayarray[index] = element.values["4"].name
          const uanFieldDisSeq = element.values["6"]
          void uanFieldDisSeq
          const uanFieldLable = element.values["8"]
          uanAllLabels[index] = uanFieldLable
          const uanFieldIntDes = element.values["8"]
          uanFieldIntDesArray[index] = element.values["8"]
          const uanFieldHlpTxtHeading = element.values["9"]
          const uanFieldHlpTxtInst = element.values["10"]
          utRgxNumber[index] = element.values["11"]
          UtilityAccountNumberRegexErrorMsg[index] = element.values["12"]
          const uanFieldHelpImgTxt = element.values["13"]
          let uanHelpImg = ""
          if (element.values["14"] !== undefined) {
            uanHelpImg = element.values["14"].url
          }
          const uanConctSeq = element.values["15"]
          void uanConctSeq
          const appendField =
            '<div class="utilityAccountNoWrapper" id="utilityAccountNoWrapper' +
            index +
            '"><label id="MUuaclabel' +
            index +
            '"><span id="utilityAccountNoLabel' +
            index +
            '">' +
            uanFieldLable +
            '</span></label><input type="text" key="' +
            index +
            '" placeholder="' +
            uanFieldIntDes +
            '" class="utilityAccountNo" id="utilityAccountNo' +
            index +
            '" /><div id="utility_acc_num_error_message' +
            index +
            '" class="error-message"></div><p id="utilityAccInsTitle' +
            index +
            '">' +
            uanFieldHlpTxtHeading +
            '</p><p><span class="utilityAccIns' +
            index +
            '">' +
            uanFieldHlpTxtInst +
            '</span><br><br><a href="' +
            uanHelpImg +
            '" id="utilityBillImageAcNo' +
            index +
            '" target="_blank">' +
            uanFieldHelpImgTxt +
            ' <span class="utilityName' +
            index +
            '">' +
            utilityNameForUANConfig +
            "</span>.</a></p></div>"
          const existingContent = q(".utilityPictureIpiaap3Wrapper #utility").innerHTML
          const appendedContent = existingContent + appendField
          q(".utilityPictureIpiaap3Wrapper #utility").innerHTML = appendedContent
          // getUtilityNumberRegex(uanFieldReg);
        } else if (uanFieldDisplay === "No") {
          uanFieldDefault[index] = element.values["5"]
          uanFieldDisplayarray[index] = element.values["4"].name
          uanFieldIntDesArray[index] = element.values["8"]
        }
      })
      uanSingleErrorMsgField = q("#utility_acc_num_error_message0")
      if (uanSingleErrorMsgField == null || uanSingleErrorMsgField === "null") {
        uanSingleErrorMsgField = q("#utility_acc_num_error_message1")
      }
      if (uanSingleErrorMsgField == null || uanSingleErrorMsgField === "null") {
        uanSingleErrorMsgField = q("#utility_acc_num_error_message2")
      }
      clickutilityAccountInputs = qa("#utility .utilityAccountNoWrapper .utilityAccountNo")
      const utilityAccountInputs = qa("#utility .utilityAccountNoWrapper .utilityAccountNo")
      utilityAccountInputs.forEach((input: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[input],["input"]);
        input.addEventListener("input", (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
          const keyAttributeValue = input.getAttribute("key")
          const inputValue = event.target.value
          uanSingleValue = inputValue
          utRgxNumberInput = utRgxNumber[keyAttributeValue]
          UtilityAccountNumberRegexErrorMsgInput = UtilityAccountNumberRegexErrorMsg[keyAttributeValue]
          uanSingleErrorMsgField = q(`#utility_acc_num_error_message${keyAttributeValue}`)
          validateAccountNumber(null)
        })
      })
    }
  }
  // get data from UAN config table ----------------------------------------------------

  function validateAccountNumber(abc: any): string | null {
__ea_trace("modules/usage/usage-bayou.ts","validateAccountNumber",[abc],["abc"]);
    // var utilityAccountNo = document.getElementById("utilityAccountNo").value.trim();
    if (abc != null && abc !== "") {
      uanSingleErrorMsgField.textContent = ""
      return null
    } else {
      if (uanSingleValue !== "") {
        const regex = new RegExp(utRgxNumberInput)
        if (regex.test(uanSingleValue)) {
          uanSingleErrorMsgField.textContent = ""
          return null
        } else {
          if (UtilityAccountNumberRegexErrorMsgInput == null || UtilityAccountNumberRegexErrorMsgInput === "") {
            uanSingleErrorMsgField.textContent = "Please enter a valid account number."
          } else {
            uanSingleErrorMsgField.textContent = UtilityAccountNumberRegexErrorMsgInput
          }
          return "error"
        }
      } else {
        if (uanFieldDisplayarray.length === 0) {
          // uanSingleErrorMsgField.textContent = '';
          return null
        } else {
          uanSingleErrorMsgField.textContent = "Please enter account number."
          return "error"
        }
      }
    }
  }
  function checkAllUANFieldOnClick(): string | boolean {
__ea_trace("modules/usage/usage-bayou.ts","checkAllUANFieldOnClick",[],[]);
    let inputValueBoolean = false
    // const clickutilityAccountInputs = document.querySelectorAll('#utility .utilityAccountNoWrapper .utilityAccountNo');

    for (let index = 0; index < clickutilityAccountInputs.length; index++) {
      const input = clickutilityAccountInputs[index]
      const inputValue = input.value
      const nextElement = input.nextElementSibling.textContent
      if (nextElement === "" || nextElement == null) {
        if (inputValue) {
          inputValueBoolean = false
          allAccountNumbers[index] = inputValue
        } else {
          inputValueBoolean = true
          const keyAttributeValue = input.getAttribute("key")
          q(`#utility_acc_num_error_message${keyAttributeValue}`).textContent = "Please enter account number."
          break
        }
      } else {
        inputValueBoolean = true
        break
      }
    }
    if (inputValueBoolean) {
      return "error"
    } else {
      return true
    }
  }
  function getAccountNumberErrorifExist(): string | null {
__ea_trace("modules/usage/usage-bayou.ts","getAccountNumberErrorifExist",[],[]);
    const val = validateAccountNumber(firstId)
    const val2 = checkAllUANFieldOnClick()
    if (val === "error" || val2 === "error") {
      return "error"
    } else {
      return null
    }
  }

  function validateHaveonline(): string | null {
__ea_trace("modules/usage/usage-bayou.ts","validateHaveonline",[],[]);
    const radioButtons = qa('[name="myInfoRadio"]')
    for (let i = 0; i < radioButtons.length; i++) {
      if ((radioButtons[i] as HTMLInputElement).checked) {
        _selectRadioValue = (radioButtons[i] as HTMLInputElement).value
        isChecked = true
        break
      }
    }

    if (!isChecked) {
      q("#radioNull-error-message").innerHTML = "Please select an option."
      return "error"
    } else {
      q("#radioNull-error-message").innerHTML = ""
      return null
    }
  }
  function validateRadio(): string | null {
__ea_trace("modules/usage/usage-bayou.ts","validateRadio",[],[]);
    const radioButtons = qa('[name="myInfoRadio"]')

    for (let i = 0; i < radioButtons.length; i++) {
      if ((radioButtons[i] as HTMLInputElement).checked) {
        _selectRadioValue = (radioButtons[i] as HTMLInputElement).value
        isChecked = true
        break
      }
    }

    if (!isChecked) {
      q("#radioNull-error-message").innerHTML = "Please select an option."
      return "error"
    } else {
      q("#radioNull-error-message").innerHTML = ""
      return null
    }
  }
  void validateRadio

  function fileFinalButtonValidation(): string | null {
__ea_trace("modules/usage/usage-bayou.ts","fileFinalButtonValidation",[],[]);
    if (listOfFiles == null || listOfFiles.length === 0) {
      q("#file-error-message").innerHTML = "No file selected. Please choose a file."
      return "error"
    } else {
      q("#file-error-message").innerHTML = ""
      return null
    }
  }

  // add data in deal record --------------------------------------------------------------------------------------
  async function addDataToDealRecord(fileUrl: any, skipUsage: any, _globPass: any): Promise<boolean | undefined> {
__ea_trace("modules/usage/usage-bayou.ts","addDataToDealRecord",[fileUrl,skipUsage,_globPass],["fileUrl","skipUsage","_globPass"]);
    const email = storage.get("userEmail")
    void email
    const iHaveOnlineAccountEl = qi("#iHaveOnlineAccount")
    const skipThisForNowAndGetInTouch = qi("#skipThisForNowAndGetInTouch")
    let iHaveOnlineAccount: any = iHaveOnlineAccountEl
    let skipUsageSubmission: any
    let skipBayouSubmission: any
    let credentialProvider = ""

    if (iHaveOnlineAccountEl.checked) {
      iHaveOnlineAccount = "Yes"
      credentialProvider = "Bayou"
      skipUsageSubmission = "No"
      skipBayouSubmission = "No"

      let uanLable1: any, uanLable2: any, uanLable3: any
      let uanValue1: any, uanValue2: any, uanValue3: any
      let isUANrow0Display = true
      let isUANrow1Display = true

      if (uanFieldDisplayarray[0] === "Yes") {
        if (uanAllLabels[0]) {
          uanLable1 = uanAllLabels[0]
        } else {
          uanLable1 = ""
        }
        uanValue1 = bayouAccountNo
      } else if (uanFieldDisplayarray[0] === "No") {
        isUANrow0Display = false
        if (uanFieldIntDesArray[0]) {
          uanLable1 = uanFieldIntDesArray[0]
        } else {
          uanLable1 = ""
        }

        if (uanFieldDefault[0]) {
          uanValue1 = uanFieldDefault[0]
        } else {
          uanValue1 = ""
        }
      }

      if (uanFieldDisplayarray[1] === "Yes") {
        if (uanAllLabels[1]) {
          uanLable2 = uanAllLabels[1]
        } else {
          uanLable2 = ""
        }
        if (isUANrow0Display === true) {
          uanValue2 = additionalAttributeValue
        } else {
          uanValue2 = bayouAccountNo
        }
      } else if (uanFieldDisplayarray[1] === "No") {
        isUANrow1Display = false
        if (uanFieldIntDesArray[1]) {
          uanLable2 = uanFieldIntDesArray[1]
        } else {
          uanLable2 = ""
        }
        if (uanFieldDefault[1]) {
          uanValue2 = uanFieldDefault[1]
        } else {
          uanValue2 = ""
        }
      }

      if (uanFieldDisplayarray[2] === "Yes") {
        if (uanAllLabels[2]) {
          uanLable3 = uanAllLabels[2]
        } else {
          uanLable3 = ""
        }
        if (isUANrow0Display === true && isUANrow1Display === true) {
          uanValue3 = allAccountNumbers[2]
        } else if (isUANrow0Display === false && isUANrow1Display === false) {
          uanValue3 = bayouAccountNo
        } else if (isUANrow0Display === true && isUANrow1Display === false) {
          uanValue3 = additionalAttributeValue
        } else if (isUANrow0Display === false && isUANrow1Display === true) {
          uanValue3 = additionalAttributeValue
        }
      } else if (uanFieldDisplayarray[2] === "No") {
        if (uanFieldIntDesArray[2]) {
          uanLable3 = uanFieldIntDesArray[2]
        } else {
          uanLable3 = ""
        }
        if (uanFieldDefault[2]) {
          uanValue3 = uanFieldDefault[2]
        } else {
          uanValue3 = ""
        }
      }

      // var uan2LableAddAtt = "Service Number";
      // if(additionalAttributeValue == null || additionalAttributeValue == ""){ uan2LableAddAtt = ""; }
      const dealId = storage.get("dealId")
      let concatenatedValue: any
      let catchdata: any = ""

      storage.rawSet("uan1Value", uanValue1)
      storage.rawSet("uan2Value", uanValue2)

      let manualInputReason = ""
      let manualUsage = ""
      if (manualAccountNumber === true) {
        if (credNotVerified === true) {
          manualInputReason = "Credentials Not Verified"
        } else {
          manualInputReason = "Accounts Not Found"
        }
        manualUsage = "Manual Usage"
      } else {
        manualUsage = ""
      }
      try {
        const data = {
          properties: {
            deal_id: dealId,
            do_you_have_an_account_with_utility_service_provider: iHaveOnlineAccount,
            credential_provider: credentialProvider,
            enrollmentsessionid: enrollmentSessionID,
            skip_usage_submission: skipUsageSubmission,
            bayous_utility_id: bayouUtility,
            bayous_type: bayouTokenType,
            bayous_id: bayouId,
            bayous_onboarding_token: bayouToken,
            uan_1_label: uanLable1,
            uan_1_value: uanValue1,
            uan_2_label: uanLable2,
            uan_2_value: uanValue2,
            uan_3_label: uanLable3,
            uan_3_value: uanValue3,
            meter_id: meterId,
            utility_type: meterType,
            skip_bayou_submission: skipBayouSubmission,
            rate_class: rateClass,
            // bayous_tarrif: rateClass,
            tarrif_type: rateClass,
            mfa_required: mfaRequired,
            uan_2_internal_value: additionalAttributeItem,
            bayou_get_accounts_resp: accountNumberResponse,
            manual_input_reason: manualInputReason,
            post_submission_verification_reason: manualUsage,
          },
        }
        const response = await fetch(apiUrl("UpdateDealRecord"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        })
        catchdata = data
        if (response.ok) {
          storage.rawSet("UanNumber", concatenatedValue)
          storage.rawSet("uan1Value", uanValue1)
          storage.rawSet("uan2Value", uanValue2)
          if (skipUsage != null) {
            storage.set("skipUsage", skipUsage)
          }
          storage.rawSet("utilityProvider", "Bayou")
          await checkConsentConfigurationVariation()
        }
        const result = await response.json()
        responseFromAPI = JSON.stringify(result)
      } catch (error) {
        pageFoundCloseLoader()
        console.error("Error:", error)
      }
    }
    if (skipThisForNowAndGetInTouch.checked) {
      iHaveOnlineAccount = "No"
      credentialProvider = "None"
      skipUsageSubmission = "Yes"
      skipBayouSubmission = "Yes"
      bayouAccountNo = ""
      // NOTE: `meter_id`/`utility_type`/`rate_class` were implicit-global writes
      // in the original (sloppy-mode leak — declared with no `var`). They are
      // write-only / never read, so scoping them as locals preserves behaviour.
      let meter_id: any, utility_type: any, rate_class: any
      meter_id = ""
      utility_type = ""
      rate_class = ""
      void meter_id
      void utility_type
      void rate_class

      let fileurl1: any,
        fileurl2: any,
        fileurl3: any,
        fileurl4: any,
        fileurl5: any,
        fileurl6: any,
        fileurl7: any,
        fileurl8: any,
        fileurl9: any,
        fileurl10: any,
        fileurl11: any,
        fileurl12: any
      let uanLable1: any, uanLable2: any, uanLable3: any
      let uanValue1: any, uanValue2: any, uanValue3: any
      if (fileUrl[0]) {
        fileurl1 = fileUrl[0]
      } else {
        fileurl1 = ""
      }
      if (fileUrl[1]) {
        fileurl2 = fileUrl[1]
      } else {
        fileurl2 = ""
      }
      if (fileUrl[2]) {
        fileurl3 = fileUrl[2]
      } else {
        fileurl3 = ""
      }
      if (fileUrl[3]) {
        fileurl4 = fileUrl[3]
      } else {
        fileurl4 = ""
      }
      if (fileUrl[4]) {
        fileurl5 = fileUrl[4]
      } else {
        fileurl5 = ""
      }
      if (fileUrl[5]) {
        fileurl6 = fileUrl[5]
      } else {
        fileurl6 = ""
      }
      if (fileUrl[6]) {
        fileurl7 = fileUrl[6]
      } else {
        fileurl7 = ""
      }
      if (fileUrl[7]) {
        fileurl8 = fileUrl[7]
      } else {
        fileurl8 = ""
      }
      if (fileUrl[8]) {
        fileurl9 = fileUrl[8]
      } else {
        fileurl9 = ""
      }
      if (fileUrl[9]) {
        fileurl10 = fileUrl[9]
      } else {
        fileurl10 = ""
      }
      if (fileUrl[10]) {
        fileurl11 = fileUrl[10]
      } else {
        fileurl11 = ""
      }
      if (fileUrl[11]) {
        fileurl12 = fileUrl[11]
      } else {
        fileurl12 = ""
      }
      let isUANrow0Display = true
      let isUANrow1Display = true

      if (uanFieldDisplayarray[0] === "Yes") {
        if (uanAllLabels[0]) {
          uanLable1 = uanAllLabels[0]
        } else {
          uanLable1 = ""
        }
        uanValue1 = allAccountNumbers[0]
      } else if (uanFieldDisplayarray[0] === "No") {
        isUANrow0Display = false
        if (uanFieldIntDesArray[0]) {
          uanLable1 = uanFieldIntDesArray[0]
        } else {
          uanLable1 = ""
        }

        if (uanFieldDefault[0]) {
          uanValue1 = uanFieldDefault[0]
        } else {
          uanValue1 = ""
        }
      }

      if (uanFieldDisplayarray[1] === "Yes") {
        if (uanAllLabels[1]) {
          uanLable2 = uanAllLabels[1]
        } else {
          uanLable2 = ""
        }
        if (isUANrow0Display === true) {
          uanValue2 = allAccountNumbers[1]
        } else {
          uanValue2 = allAccountNumbers[0]
        }
      } else if (uanFieldDisplayarray[1] === "No") {
        isUANrow1Display = false
        if (uanFieldIntDesArray[1]) {
          uanLable2 = uanFieldIntDesArray[1]
        } else {
          uanLable2 = ""
        }
        if (uanFieldDefault[1]) {
          uanValue2 = uanFieldDefault[1]
        } else {
          uanValue2 = ""
        }
      }

      if (uanFieldDisplayarray[2] === "Yes") {
        if (uanAllLabels[2]) {
          uanLable3 = uanAllLabels[2]
        } else {
          uanLable3 = ""
        }
        if (isUANrow0Display === true && isUANrow1Display === true) {
          uanValue3 = allAccountNumbers[2]
        } else if (isUANrow0Display === false && isUANrow1Display === false) {
          uanValue3 = allAccountNumbers[0]
        } else if (isUANrow0Display === true && isUANrow1Display === false) {
          uanValue3 = allAccountNumbers[1]
        } else if (isUANrow0Display === false && isUANrow1Display === true) {
          uanValue3 = allAccountNumbers[1]
        }
      } else if (uanFieldDisplayarray[2] === "No") {
        if (uanFieldIntDesArray[2]) {
          uanLable3 = uanFieldIntDesArray[2]
        } else {
          uanLable3 = ""
        }
        if (uanFieldDefault[2]) {
          uanValue3 = uanFieldDefault[2]
        } else {
          uanValue3 = ""
        }
      }

      const email = storage.get("userEmail")
      void email
      const dealId = storage.get("dealId")
      let concatenatedValue: any
      if (uanValue1 != null && uanValue1 !== "") {
        concatenatedValue = uanValue1
      }
      if (uanValue2 != null && uanValue2 !== "") {
        concatenatedValue += `-${uanValue2}`
      }
      if (uanValue3 != null && uanValue3 !== "") {
        concatenatedValue += `-${uanValue3}`
      }

      const property = "combined_utility_account_number"
      const value = concatenatedValue
      const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ property, value }),
      })
      const data = await response.json()
      responseFromAPI = JSON.stringify(data)
      if (response.ok) {
        if (data.total === 0 || data.total === undefined) {
          storage.rawSet("UanNumber", concatenatedValue)
          storage.rawSet("uan1Value", uanValue1)
          storage.rawSet("uan2Value", uanValue2)
          let catchdata: any = ""

          let manualInputReason = ""
          let manualUsage = ""
          if (manualAccountNumber === true) {
            if (credNotVerified === true) {
              manualInputReason = "Credentials Not Verified"
            } else {
              manualInputReason = "Accounts Not Found"
            }
            manualUsage = "Manual Usage"
          } else {
            manualUsage = ""
          }
          try {
            const data = {
              properties: {
                deal_id: dealId,
                do_you_have_an_account_with_utility_service_provider: iHaveOnlineAccount,
                utility_bill_file_1_url: fileurl1,
                utility_bill_file_2_url: fileurl2,
                utility_bill_file_3_url: fileurl3,
                utility_bill_file_4_url: fileurl4,
                utility_bill_file_5_url: fileurl5,
                utility_bill_file_6_url: fileurl6,
                utility_bill_file_7_url: fileurl7,
                utility_bill_file_8_url: fileurl8,
                utility_bill_file_9_url: fileurl9,
                utility_bill_file_10_: fileurl10,
                utility_bill_file_11_: fileurl11,
                utility_bill_file_12_: fileurl12,
                credential_provider: credentialProvider,
                enrollmentsessionid: enrollmentSessionID,
                skip_usage_submission: skipUsageSubmission,
                bayous_utility_id: bayouUtility,
                bayous_type: bayouTokenType,
                bayous_id: bayouId,
                bayous_onboarding_token: bayouToken,
                uan_1_label: uanLable1,
                uan_1_value: uanValue1,
                uan_2_label: uanLable2,
                uan_2_value: uanValue2,
                uan_3_label: uanLable3,
                uan_3_value: uanValue3,
                meter_id: meterId,
                utility_type: meterType,
                skip_bayou_submission: skipBayouSubmission,
                rate_class: rateClass,
                // bayous_tarrif: rateClass,
                tarrif_type: rateClass,
                mfa_required: mfaRequired,
                uan_2_internal_value: additionalAttributeItem,
                manual_input_reason: manualInputReason,
                post_submission_verification_reason: manualUsage,
              },
            }
            const response = await fetch(apiUrl("UpdateDealRecord"), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data),
            })
            catchdata = data
            if (response.ok) {
              if (skipUsage != null) {
                storage.set("skipUsage", skipUsage)
              }
              storage.rawSet("utilityProvider", "Bayou")
              await checkConsentConfigurationVariation()
            }
            const result = await response.json()
            responseFromAPI = JSON.stringify(result)
          } catch (error) {
            pageFoundCloseLoader()
            console.error("Error:", error)
          }
          return true
        } else {
          q("#accounterror").innerHTML = "Account number already exists. Please try again."
          pageFoundCloseLoader()
          return false
        }
        // Process and display the data as needed
      }
    }
    storage.rawSet("uan_2_internal_value", additionalAttributeItem)
  }
  async function saveallkeypiecesfromcompleteddeal(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","saveallkeypiecesfromcompleteddeal",[],[]);
    const contactId = storage.get("RecordId")
    const deallId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        contactId: contactId,
      }
      const response = await fetch(apiUrl("FetchAssociatedDealsBasedOnContactId"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const dealRecordLength = dataa.userValue.associations.deals.results.length
      void dealRecordLength
      const dealIdLog = dataa.userValue.associations.deals.results

      for (let i = 0; i < dealIdLog.length; i++) {
        if (deallId !== dealIdLog[i].id) {
          const deal = dealIdLog[i].id
          let catchdata: any = ""
          try {
            const data = {
              deal_id: deal,
            }
            const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data),
            })
            catchdata = data
            const dataa = await response.json()
            responseFromAPI = JSON.stringify(dataa)
            const l_dealStage = dataa.userValue.properties.dealstage
            const credential_provider = dataa.userValue.properties.credential_provider
            const l_dealId = dataa.userValue.id
            void l_dealId
            if (
              l_dealStage === 127892578 /* completed */ ||
              l_dealStage === 127892576 /* Interfaced (Sales Pipeline) */ ||
              l_dealStage === 127892575 /* Staged (Sales Pipeline) */ ||
              l_dealStage === 127892570 /* Submitted (Sales Pipeline) */ ||
              l_dealStage === 127892574 /* Verified (Sales Pipeline) */ ||
              l_dealStage === 127892573 /* Verify HU (Sales Pipeline) */
            ) {
              if (credential_provider === "Bayou") {
                const bayous_onboarding_tokenn = dataa.userValue.properties.bayous_onboarding_token
                const utility_usernamee = dataa.userValue.properties.utility_username
                const utility_passwordd = dataa.userValue.properties.utility_password
                const bayous_id = dataa.userValue.properties.bayous_id
                const bayous_type = dataa.userValue.properties.bayous_type
                const bayou_get_bills_sent = dataa.userValue.properties.bayou_get_bills_sent
                const bayou_get_bills_resp = dataa.userValue.properties.bayou_get_bills_resp
                const bill_status = dataa.userValue.properties.bill_status
                const bayous_utility_id = dataa.userValue.properties.bayous_utility_id
                let catchdata: any = ""
                try {
                  const data = {
                    properties: {
                      deal_id: deallId,
                      bayous_onboarding_token: bayous_onboarding_tokenn,
                      utility_username: utility_usernamee,
                      utility_password: utility_passwordd,
                      bayous_id: bayous_id,
                      bayous_type: bayous_type,
                      bayou_get_bills_sent: bayou_get_bills_sent,
                      bayou_get_bills_resp: bayou_get_bills_resp,
                      bill_status: bill_status,
                      bayous_utility_id: bayous_utility_id,
                    },
                  }
                  const response = await fetch(apiUrl("UpdateDealRecord"), {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify(data),
                  })
                  catchdata = data
                  const dataa = await response.json()
                  responseFromAPI = JSON.stringify(dataa)
                  if (response.ok) {
                  }

                  // const result = await response.json();
                  // responseFromAPI = JSON.stringify(result);
                } catch (error) {
                  console.error("Error:", error)
                }
              }
            } else {
            }
          } catch (error) {
            console.error("Error:", error)
            // enterZipcloseLoader();
          }
        } else {
        }
      }
    } catch (error) {
      // enterZipcloseLoader();
      console.error("Error:", error)
    }
  }
  // Get utility button click ----------------------------------------------------------
  q("#get-utility-info").addEventListener("click", () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
    const iHaveOnlineAccountCheck = qi("#iHaveOnlineAccount")
    const skipThisForNowAndGetInTouchCheck = qi("#skipThisForNowAndGetInTouch")
    if (iHaveOnlineAccountCheck.checked) {
      pageFoundOpenLoader()
      const ErrorValidationList = [validateHaveonline()]
      if (ErrorValidationList.includes("error")) {
        pageFoundCloseLoader()
        q("#finalCheck-error").innerHTML = "please fix the error before submitting"
      } else {
        q("#finalCheck-error").innerHTML = ""
        const iHaveOnlineAccount = qi("#iHaveOnlineAccount")
        // var iDontHaveOnlineAcc = document.getElementById("iDontHaveOnlineAcc");
        const skipThisForNowAndGetInTouch = qi("#skipThisForNowAndGetInTouch")
        if (iHaveOnlineAccount.checked) {
          if (verifyidenty === "yes") {
            ;(async () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
              try {
                // const url = await uploadFileToServer();
                const url = ""
                const skipUsage = "No"
                addDataToDealRecord(url, skipUsage, globPass)
              } catch (error) {
                pageFoundCloseLoader()
                alert(error)
                window.location.href = "/myusage"
              }
            })()
          } else if (verifyidenty === "no") {
            pageFoundCloseLoader()
            q("#verification-error-message").innerHTML = "please fix the error before submitting"
            const sectionId = "haveOnlineAccountMainDiv"
            const section = q(`#${sectionId}`)
            if (section) {
              section.scrollIntoView({ behavior: "smooth" })
            }
          } else if (verifyidenty === undefined) {
            pageFoundCloseLoader()
            q("#verification-error-message").innerHTML = "please fix the error before submitting"
            const sectionId = "haveOnlineAccountMainDiv"
            const section = q(`#${sectionId}`)
            if (section) {
              section.scrollIntoView({ behavior: "smooth" })
            }
          }
        } else if (skipThisForNowAndGetInTouch.checked) {
          ;(async () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
            try {
              const url = await uploadFileToServer()
              // const url = "";
              const skipUsage = "Yes"
              addDataToDealRecord(url, skipUsage, addDataToDealRecord)
            } catch (error) {
              pageFoundCloseLoader()
              alert(error)
              window.location.href = "/myusage"
            }
          })()
        }
      }
    } else if (skipThisForNowAndGetInTouchCheck.checked) {
      pageFoundOpenLoader()
      const ErrorValidationList = [validateHaveonline(), fileFinalButtonValidation(), getAccountNumberErrorifExist()]
      if (ErrorValidationList.includes("error")) {
        pageFoundCloseLoader()
        q("#finalCheck-error").innerHTML = "please fix the error before submitting"
      } else {
        q("#finalCheck-error").innerHTML = ""
        const iHaveOnlineAccount = qi("#iHaveOnlineAccount")
        // var iDontHaveOnlineAcc = document.getElementById("iDontHaveOnlineAcc");
        const skipThisForNowAndGetInTouch = qi("#skipThisForNowAndGetInTouch")
        if (iHaveOnlineAccount.checked) {
          if (verifyidenty === "yes") {
            ;(async () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
              try {
                // const url = await uploadFileToServer();
                const url = ""
                const skipUsage = "No"
                addDataToDealRecord(url, skipUsage, globPass)
              } catch (error) {
                pageFoundCloseLoader()
                alert(error)
                window.location.href = "/myusage"
              }
            })()
          } else if (verifyidenty === "no") {
            pageFoundCloseLoader()
            q("#verification-error-message").innerHTML = "please fix the error before submitting"
            const sectionId = "haveOnlineAccountMainDiv"
            const section = q(`#${sectionId}`)
            if (section) {
              section.scrollIntoView({ behavior: "smooth" })
            }
          } else if (verifyidenty === undefined) {
            pageFoundCloseLoader()
            q("#verification-error-message").innerHTML = "please fix the error before submitting"
            const sectionId = "haveOnlineAccountMainDiv"
            const section = q(`#${sectionId}`)
            if (section) {
              section.scrollIntoView({ behavior: "smooth" })
            }
          }
        } else if (skipThisForNowAndGetInTouch.checked) {
          ;(async () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
            try {
              const url = await uploadFileToServer()
              // const url = "";
              const skipUsage = "Yes"
              addDataToDealRecord(url, skipUsage, addDataToDealRecord)
            } catch (error) {
              pageFoundCloseLoader()
              alert(error)
              window.location.href = "/myusage"
            }
          })()
        }
      }
    }
  })
  // Get utility button click ----------------------------------------------------------
  // add data in deal record ------------------------------------------------------------------------------------

  // Save my place and remind me --------------------------------------------------------------------------------
  async function saveDataToDealRecord(fileUrl?: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou.ts","saveDataToDealRecord",[fileUrl],["fileUrl"]);
    void fileUrl
    const email = storage.get("userEmail")
    void email
    const iHaveOnlineAccount0 = qi("#iHaveOnlineAccount")
    let iHaveOnlineAccount: any = iHaveOnlineAccount0
    let credentialProvider = ""
    if (iHaveOnlineAccount0.checked) {
      iHaveOnlineAccount = "Yes"
      credentialProvider = "Bayou"
    } else {
      iHaveOnlineAccount = "No"
      credentialProvider = "None"
    }
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          do_you_have_an_account_with_utility_service_provider: iHaveOnlineAccount,
          credential_provider: credentialProvider,
          enrollmentsessionid: enrollmentSessionID,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  q("#save-remind").addEventListener("click", () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
    ;(async () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
      try {
        const url = ""
        saveDataToDealRecord(url)
      } catch (error) {
        saveDataToDealRecord()
      }
    })()
  })
  // Save my place and remind me --------------------------------------------------------------------------------

  // get utility name and logo from session config table ----------------------------------------------------
  function getDataFromSessionConfigTable(utilityIdd: any): void {
__ea_trace("modules/usage/usage-bayou.ts","getDataFromSessionConfigTable",[utilityIdd],["utilityIdd"]);
    // live site table id - 15223637
    const hubDBEndpoint = hubdbRowsUrl("utilityAvailability", {
      limit: limit,
      offset: offset,
    })
    fetch(hubDBEndpoint, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[data],["data"]);
        const matchingRows = data.objects.filter((row: any) => row.values[1] === utilityIdd)
        const rows = data.objects
        if (matchingRows.length >= 1) {
          allRows = allRows.concat(matchingRows)
        }
        if (rows.length === limit) {
          // There might be more rows, fetch the next page
          offset += limit
          return getDataFromSessionConfigTable(utilityIdd)
        }

        const utilityName = allRows[0].values[5]
        const utilityLogoImage = allRows[0].values[17].url
        const forgetPw = allRows[0].values[27]
        const forgetUs = allRows[0].values[28]
        const CreateAnAccBtn = allRows[0].values[29]
        const utiNameItem = root.getElementsByClassName("utilityName")
        const forgetPwitem = q("#forgetPw") as HTMLAnchorElement
        const forgetUsitem = q("#forgetUs") as HTMLAnchorElement
        const createAnAccountItem = q("#createAnAccount") as HTMLAnchorElement
        // --------------------------------
        if (utilityName !== undefined && utilityName !== "undefined") {
          if (utiNameItem.length > 0) {
            for (let i = 0; i < utiNameItem.length; i++) {
              ;(utiNameItem[i] as HTMLElement).innerText = utilityName
            }
          }
        }
        // --------------------------------
        if (utilityLogoImage !== undefined) {
          ;(q("#utilityLogoImage") as HTMLImageElement).src = utilityLogoImage
          q("#utilityName").innerText = ""
        } else {
          q("#utilityName").innerText = utilityName
          // NOTE: `getElementsById` is a typo (no such method) — throws if reached;
          // preserved via cast (unreachable unless the logo url is undefined).
          ;(document as any).getElementsById("utilityLogoImage").src = ""
        }
        // --------------------------------
        if (forgetPw.startsWith("https://")) {
          forgetPwitem.href = forgetPw
        } else if (forgetPw.startsWith("http://")) {
          forgetPwitem.href = forgetPw
        } else {
          forgetPwitem.href = `https://${forgetPw}`
        }
        // --------------------------------
        if (forgetUs.startsWith("https://")) {
          forgetUsitem.href = forgetUs
        } else if (forgetUs.startsWith("http://")) {
          forgetUsitem.href = forgetUs
        } else {
          forgetUsitem.href = `https://${forgetUs}`
        }
        // --------------------------------
        if (CreateAnAccBtn.startsWith("https://")) {
          createAnAccountItem.href = CreateAnAccBtn
        } else if (CreateAnAccBtn.startsWith("http://")) {
          createAnAccountItem.href = CreateAnAccBtn
        } else {
          createAnAccountItem.href = `https://${CreateAnAccBtn}`
        }
        // --------------------------------
      })
      .catch(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
        // console.error('Error fetching data from HubDB:', error);
      })
  }
  getDataFromSessionConfigTable(utilityIdd)
  // get utility name and logo from session config table ----------------------------------------------------

  // get data from UAN config table ----------------------------------------------------
  async function getDataFromUANConfigTable(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","getDataFromUANConfigTable",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const stateForUANConfig = storage.get("stateForLMIConfig")
    const utilityNameForUANConfig = storage.get("utility_Name")
    const hubDBEndpointUANC = hubdbRowsUrl("uanConfig", {
      orderBy: "uan_field_display_sequence",
      utility_id: String(exampleUtilityId),
      state_name: String(stateForUANConfig),
    })
    const responseUANC = await fetch(hubDBEndpointUANC, { method: "GET" })
    const dataUANC = await responseUANC.json()
    const rowsUANC = dataUANC.objects
    if (rowsUANC.length >= 1) {
      rowsUANC.forEach((element: any, index: number) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[element,index],["element","index"]);
        const uanFieldDisplaySequence = element.values["6"]
        void uanFieldDisplaySequence
        void index
        if (element.values["16"] !== undefined && element.values["16"] !== "undefined") {
          const additionalAttributes = element.values["16"]
          additionalAttributeItem = additionalAttributes
        }
      })
    }
  }
  // get data from UAN config table ----------------------------------------------------

  // -------- checked skip radio if exist on page load
  const ms_currentUrl = window.location.href
  if (ms_currentUrl.includes("?Rftyp=")) {
    q("#SkipThisFornNow").style.display = "none"
    qi("#iHaveOnlineAccount").checked = true
    const ms_updatedUrl = ms_currentUrl.replace("?Rftyp=", "")
    window.history.replaceState({}, document.title, ms_updatedUrl)
    isWeBackFromTYP = true
    storage.rawSet("isWeBackFromTYP", "yes")
  } else {
    q("#SkipThisFornNow").style.display = "block"
    isWeBackFromTYP = false
  }
  const ls_isWeBackFromTYP = storage.rawGet("isWeBackFromTYP")
  if (ls_isWeBackFromTYP != null && ls_isWeBackFromTYP === "yes") {
    q("#SkipThisFornNow").style.display = "none"
    qi("#iHaveOnlineAccount").checked = true
  }
  // -------- checked skip radio if exist on page load

  function toggleContent(radioValue: any): void {
__ea_trace("modules/usage/usage-bayou.ts","toggleContent",[radioValue],["radioValue"]);
    q("#verification-error-message").innerHTML = ""
    const haveOnlineAccountRadio = q("#have-online-details")
    q("#radioNull-error-message").innerHTML = ""
    if (radioValue.value === "haveOnline") {
      q(".utilityPictureIpiaap3Wrapper .utility").style.display = "none"
      q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "none"
      q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "none"
      haveOnlineAccountRadio.style.display = "block"
    } else {
      q(".utilityPictureIpiaap3Wrapper .utility").style.display = "block"
      q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "block"
      q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "block"
      haveOnlineAccountRadio.style.display = "none"
    }
    // Toggle visibility based on the selected radio button's value
  }
  function captureFromCamera(): void {
__ea_trace("modules/usage/usage-bayou.ts","captureFromCamera",[],[]);
    const inputElement = document.createElement("input")
    inputElement.type = "file"
    inputElement.accept = "image/*"
    inputElement.addEventListener("change", function (this: HTMLInputElement) {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[this],["this"]);
      handleFilesNew(this.files)
    })

    inputElement.click()
  }
  void captureFromCamera
  function triggerFileInput(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","triggerFileInput",[event],["event"]);
    event.preventDefault()
    q('#mobileCamera input[type="file"]').click()
  }
  q("#imageInput").addEventListener("change", (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    const file = event.target.files[0]
    void file
    // var reader = new FileReader();
    // reader.onload = function(event) { ... };
    // reader.readAsDataURL(file);
    handleFilesNew(event.target.files)
  })
  function dragOverHandler(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","dragOverHandler",[event],["event"]);
    event.preventDefault()

    q("#dropArea").classList.add("active")
  }
  function dropHandler(event: any): void {
__ea_trace("modules/usage/usage-bayou.ts","dropHandler",[event],["event"]);
    event.preventDefault()
    q("#dropArea").classList.remove("active")

    const files = event.dataTransfer.files
    q("#file-error-message").innerHTML = ""
    if (files.length > 0) {
      handleFilesNew(files)
    }
  }
  function browseFiles(): void {
__ea_trace("modules/usage/usage-bayou.ts","browseFiles",[],[]);
    const inputElement = document.createElement("input")
    q("#file-error-message").innerHTML = ""

    inputElement.type = "file"
    inputElement.accept = "application/pdf, image/*, image/heic, image/bmp, image/webp"
    inputElement.multiple = true
    // inputElement.addEventListener("change", function () { handleFilesNew(this.files); });
    inputElement.addEventListener("change", (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
      handleFilesNew(event.target.files)
    })
    inputElement.click()
  }
  void browseFiles

  // var file;
  function handleFilesNew(files: any): void {
__ea_trace("modules/usage/usage-bayou.ts","handleFilesNew",[files],["files"]);
    const maxFiles = 12
    let totalfilecount = 0
    totalfilecount = listOfFiles.length + files.length

    if (files.length > 0 && totalfilecount <= maxFiles) {
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/heic", "image/bmp", "image/webp"]
      const totalSize = 0
      void totalSize

      let file: any
      for (let i = 0; i < files.length; i++) {
        file = files[i]

        if (!allowedTypes.includes(file.type)) {
          q("#file-error-message").innerHTML =
            "Invalid file type. Please choose valid PDF, PNG, JPEG, HEIC, BMP, or WebP files."
          return
        }
      }

      if (file.size > 10 * 1024 * 1024) {
        q("#file-error-message").innerHTML =
          `The total size of selected files exceeds the limit. Please choose files up to 10 MB in total.`
        return
      }

      const fileNameDisplay = q("#fileNameDisplay")
      const previewContainer = q("#previewContainer")

      for (let j = 0; j < files.length; j++) {
        const file2 = files[j]
        const reader = new FileReader()
        const preview = document.createElement("div")
        preview.className = "swiper-slide"
        preview.innerHTML = `<img src="" alt="Preview Image" width="100%" height="auto" id="imageNo${j}">`
        previewContainer.appendChild(preview)

        reader.onload = ((preview: any, file: any) => (e: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[e],["e"]);
          if (file.type === "application/pdf") {
            pdfjsLib.getDocument(e.target.result).promise.then((pdf: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[pdf],["pdf"]);
              pdf.getPage(1).then((page: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[page],["page"]);
                const canvas = document.createElement("canvas")
                const context = canvas.getContext("2d")
                const viewport = page.getViewport({ scale: 1.5 })
                canvas.height = viewport.height
                canvas.width = viewport.width
                const renderContext = {
                  canvasContext: context,
                  viewport: viewport,
                }
                page.render(renderContext).promise.then(() => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
                  ;(preview.querySelector("img") as HTMLImageElement).src = canvas.toDataURL("image/jpeg")
                })
              })
            })
          } else {
            ;(preview.querySelector("img") as HTMLImageElement).src = e.target.result
          }

          const blobUrl = URL.createObjectURL(file)
          void blobUrl
          // Add an event listener to open the image in a new tab when clicked
          // preview.querySelector('img').addEventListener('click', function () { ... });
          initSwiper() // Initialize Swiper after adding each image
        })(preview, file2)

        reader.readAsDataURL(file2)
        listOfFiles.push(file2)

        // Save file type and Base64 to localStorage (kept commented in original)
      }

      fileNameDisplay.textContent = `File Name: ${listOfFiles.map((f: any) => f.name).join(", ")}`
      q("#fileCount").textContent = `Total files selected: ${listOfFiles.length}`
    } else {
      q("#file-error-message").innerHTML = `Please select between 1 and ${maxFiles} files.`
    }
  }
  function initSwiper(): void {
__ea_trace("modules/usage/usage-bayou.ts","initSwiper",[],[]);
    swiper = new Swiper(".swiper-container", {
      slidesPerView: "auto",
      spaceBetween: 10,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
    })
    q(".swiper-button-next").innerHTML = "<span>Next</span>"
    q(".swiper-button-prev").innerHTML = "<span>Previous</span>"
  }
  function removeCurrentImage(): void {
__ea_trace("modules/usage/usage-bayou.ts","removeCurrentImage",[],[]);
    // q('.swiper-slide.swiper-slide-active').remove;
    const activeIndex = swiper.activeIndex
    q("#file-error-message").innerHTML = ""

    listOfFiles.splice(activeIndex, 1) // Remove the file from the list
    swiper.removeSlide(activeIndex) // Remove the slide from Swiper
    q("#fileCount").textContent = `Total files selected: ${listOfFiles.length}`

    initSwiper() // Reinitialize Swiper

    const fileNameDisplay = q("#fileNameDisplay")
    fileNameDisplay.textContent = `File Name: ${listOfFiles.map((f: any) => f.name).join(", ")}`

    q(".RemoveFilePopup").style.display = "none"
  }
  function removeImageOpenPopup(): void {
__ea_trace("modules/usage/usage-bayou.ts","removeImageOpenPopup",[],[]);
    q(".RemoveFilePopup").style.display = "block"
  }
  function removeImageClosePopup(): void {
__ea_trace("modules/usage/usage-bayou.ts","removeImageClosePopup",[],[]);
    q(".RemoveFilePopup").style.display = "none"
  }

  // -- uploaded file to file manager and return file url ---------------------------------------------------
  async function uploadFileToServer(): Promise<any[]> {
__ea_trace("modules/usage/usage-bayou.ts","uploadFileToServer",[],[]);
    clearDatabase()

    // Use the global variable listOfFiles to send files in the API call
    const uploadedUrls: any[] = [] // Array to store uploaded URLs
    for (const file of listOfFiles) {
      try {
        const formData = new FormData()
        const originalName = file.name

        const safeName = originalName
          .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
          .replace(/[^\x00-\x7F]/g, "")
          .replace(/\s+/g, " ")

        formData.append("file", file, safeName)
        formData.append("name", safeName)

        const requestOptions = {
          method: "POST",
          body: formData,
        }

        // Make the API call
        // EXTERNAL AWS file-manager upload service — no __ENV__ key exists, kept as literal.
        const response = await fetch("https://aws.nautilussolar.com/testRoute/uploadFileToFileManager", requestOptions)
        // https://aws.nautilussolar.com:5000/testRoute/uploadFileToFileManager
        // https://nidish.us:5000/testRoute/uploadFileToFileManager
        const result = await response.json()
        responseFromAPI = JSON.stringify(result)
        if (response.ok) {
          saveFileToIndexedDB(file)
          uploadedUrls.push(result.response.url)
        } else {
          throw new Error("Error uploading file")
        }
      } catch (error) {
        throw error // Throw the error to be caught by the calling code
      }
    }
    return uploadedUrls
  }
  // update Deal Stage -------------------------------------------------------------------------------
  async function updateDealStage(value: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou.ts","updateDealStage",[value],["value"]);
    await delay(2000)
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: value,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  //- update deaLstage -------------------------------------------------------------------------------
  async function delay(ms: number): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","delay",[ms],["ms"]);
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  async function checkConsentConfigurationVariation(): Promise<void> {
__ea_trace("modules/usage/usage-bayou.ts","checkConsentConfigurationVariation",[],[]);
    const is_enrolled_for_bayou = storage.rawGet("is_enrolled_for_bayou")
    if (is_enrolled_for_bayou === "yes") {
      await saveallkeypiecesfromcompleteddeal()
    }

    let consetPaymentReq: any
    const SessionLocalStorage = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    if (SessionLocalStorage["33"] === 0) {
      consetPaymentReq = "No"
    } else if (SessionLocalStorage["33"] === 1) {
      consetPaymentReq = "Yes"
    } else {
      consetPaymentReq = "No"
    }
    const consentDisplayelementList = JSON.parse(storage.get("consentDisplayelementList") ?? "null")
    if (consentDisplayelementList != null) {
      if (consentDisplayelementList.length > 0) {
        if (consetPaymentReq === "Yes") {
          await updateDealStage("127892568")
          storage.rawSet("rp", "QxtVq")
          pageFoundCloseLoader()
          window.location.href = "/my-consent"
        } else {
          await updateDealStage("127892567")
          storage.rawSet("rp", "NpEv")
          pageFoundCloseLoader()
          window.location.href = "/my-consent"
        }
      }
    }
  }

  // IndexedDB Part
  const request = indexedDB.open("myDatabase", 1)

  request.onerror = (event) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    console.log(`Database error: ${(event.target as any).errorCode}`)
  }

  request.onsuccess = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    db = event.target.result
    getAllFilesFromIndexedDB()
  }

  request.onupgradeneeded = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    const db = event.target.result

    // Create an object store (similar to a table in relational databases)
    const objectStore = db.createObjectStore("files", { autoIncrement: true })

    // Optionally, you can create indexes for efficient querying
    objectStore.createIndex("name", "name", { unique: false })
  }

  function saveFileToIndexedDB(file: any): void {
__ea_trace("modules/usage/usage-bayou.ts","saveFileToIndexedDB",[file],["file"]);
    const reader = new FileReader()

    reader.onload = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
      const arrayBuffer = event.target.result
      // Create an object to store both the file name and the ArrayBuffer
      const fileObject = {
        name: file.name,
        type: file.type,
        data: arrayBuffer,
      }
      const transaction = db.transaction(["files"], "readwrite")
      const objectStore = transaction.objectStore("files")

      // Save the ArrayBuffer in the object store
      const request = objectStore.put(fileObject)

      request.onerror = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
        console.log(`Database error: ${(event.target as any).errorCode}`)
      }
    }

    reader.onerror = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
      console.error("Error reading file:", event.target.error)
    }

    // Read the file as ArrayBuffer
    reader.readAsArrayBuffer(file)
  }

  function getAllFilesFromIndexedDB(): void {
__ea_trace("modules/usage/usage-bayou.ts","getAllFilesFromIndexedDB",[],[]);
    const transaction = db.transaction(["files"], "readonly")
    const objectStore = transaction.objectStore("files")
    const filess: any[] = []
    let index = 0

    // Open a cursor to iterate over all objects in the object store
    objectStore.openCursor().onsuccess = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
      const cursor = event.target.result
      if (cursor) {
        const fileName = cursor.value.name
        const blobData = cursor.value.data
        const fileType = cursor.value.type
        const file = new File([blobData], fileName, { type: fileType }) // Provide a default type
        index++
        filess.push(file)
        cursor.continue()
      } else {
        // All files have been retrieved
        if (index !== 0) {
          handleFilesNew(filess)
        }
        // Here you can do whatever you want with the files, like displaying them in a list
      }
    }
  }

  // Function to clear the entire IndexedDB database
  function clearDatabase(): void {
__ea_trace("modules/usage/usage-bayou.ts","clearDatabase",[],[]);
    const transaction = db.transaction(db.objectStoreNames, "readwrite")

    transaction.oncomplete = () => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[],[]);
    }

    transaction.onerror = (event: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[event],["event"]);
    }

    Array.from(db.objectStoreNames).forEach((storeName: any) => {
__ea_trace("modules/usage/usage-bayou.ts","(anon)",[storeName],["storeName"]);
      const objectStore = transaction.objectStore(storeName)
      objectStore.clear()
    })
  }
  // End of Indexed Db///

  if (is_enrolled_for_bayou === "yes") {
    GetDataFromDealRecordMulti()
    getallDataFromUANConfigTable()
    q("#have-online-details").style.display = "block"
  } else {
    GetDataFromDealRecord()
    getallDataFromUANConfigTable()
  }

  q("#SkipThisFornNow").style.display = "none"
  q(".utilityPictureIpiaap3Wrapper .utility").style.display = "none"
  q(".utilityPictureIpiaap3Wrapper #accounterror").style.display = "none"
  q(".utilityPictureIpiaap3Wrapper #upload-picture-main").style.display = "none"


  // ─── wire the (formerly inline) handlers ─────────────────────────────────────
  // The markup no longer carries onclick/ondrop/ondragover attributes; the former
  // inline handlers are bound here, root-scoped, so no page function leaks onto
  // window. The two commented-out camera blocks (captureFromCamera / bare
  // triggerFileInput) remain commented in markup and are not bound.
  const bind = (sel: string, type: string, fn: (e: any) => void) => {
__ea_trace("modules/usage/usage-bayou.ts","bind",[sel,type,fn],["sel","type","fn"]);
    const el = root.querySelector(sel)
    if (el) el.addEventListener(type, fn)
  }

  // online-account radios → toggleContent(this)
  bind("#iHaveOnlineAccount", "click", (e) => toggleContent(e.currentTarget))
  bind("#skipThisForNowAndGetInTouch", "click", (e) => toggleContent(e.currentTarget))

  // bill-upload group
  bind("#dropArea", "drop", (e) => dropHandler(e))
  bind("#dropArea", "dragover", (e) => dragOverHandler(e))
  bind("#dropArea", "click", () => triggerFilePicker())
  bind("#mobileCamera img", "click", (e) => triggerFileInput(e))
  bind("#mobileCamera a", "click", (e) => triggerFileInput(e))
  bind(".uploadRemoveButtons button:not([id])", "click", () => triggerFilePicker())
  bind("#removeimagebutton", "click", () => removeImageOpenPopup())
  bind(".removefilePopButtons button:not([id])", "click", () => removeCurrentImage())
  bind("#keepIt", "click", () => removeImageClosePopup())
}
